import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users, Building2, ClipboardList, MessageSquare, CheckCircle, Loader2,
  Wrench, ShieldCheck, UserCheck, Clock, AlertTriangle, Search,
  CreditCard, Link2, PoundSterling, Trash2, FileText, Info, Clipboard, Heart, Trash,
  RefreshCw, Bell, Download, FileCheck, Activity, HeartPulse, Sparkles, Mail, TrendingUp, ShieldAlert, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { scrollToElementWithOffset } from '../lib/scroll';
import { AdminDocumentAuditTab } from '../components/AdminDocumentAuditTab';
import { AdminDocumentDeliveryTab } from '../components/AdminDocumentDeliveryTab';
import { AdminComplianceTab } from '../components/AdminComplianceTab';
import { AdminSigningTab } from '../components/AdminSigningTab';
import { AdminMarketRateHistoryTab } from '../components/AdminMarketRateHistoryTab';
import { AdminEngagementDashboard } from './AdminEngagementDashboard';
import { AddressInput } from '../components/AddressInput';
import { DEFAULT_ISSUE_CATEGORY, ISSUE_CATEGORIES, ISSUE_CATEGORY_OPTIONS } from '../lib/issueCategories';
import { CATEGORY_ICONS } from '../lib/issueCategoryIcons';
import { getIssueDescriptionTemplate } from '../lib/issueFormHelpers';
import { validatePersonFullName } from '../lib/personNames';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TAB_PAGE_SIZE = 50;

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const unwrapApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeNonNegativeIntegerInput = (value) => {
  if (value === '') return '';
  const digitsOnly = String(value).replace(/[^0-9]/g, '');
  if (!digitsOnly) return '';
  return String(parseInt(digitsOnly, 10));
};

const getRentScheduleChainKey = (rent) => `${rent.property_id}::${rent.tenant_id || rent.manual_tenancy_id || '__unassigned__'}`;

const getLatestRentSchedulePerChain = (rents = []) => {
  const latestByChain = new Map();
  rents.forEach((rent) => {
    const key = getRentScheduleChainKey(rent);
    const existing = latestByChain.get(key);
    if (!existing || (rent.next_due_date || '') > (existing.next_due_date || '')) {
      latestByChain.set(key, rent);
    }
  });
  return Array.from(latestByChain.values());
};

const normalizeEmailValue = (value = '') => value.trim().toLowerCase();

const getPersonDisplayLabel = (name, email) => {
  const safeName = name || 'Unknown';
  return email ? `${safeName} (${email})` : safeName;
};

const getAssignmentTenants = (assignment) => {
  if (Array.isArray(assignment?.tenants) && assignment.tenants.length > 0) {
    return assignment.tenants;
  }

  return [];
};

const getAssignmentTenantSearchText = (assignment) => getAssignmentTenants(assignment)
  .flatMap((tenant) => [tenant?.name || '', tenant?.email || ''])
  .join(' ')
  .toLowerCase();

const getAssignmentPortalTenantLabel = (assignment) => {
  const participant = getAssignmentTenants(assignment).find((tenant) => tenant?.user_id === assignment?.tenant_id);
  return getPersonDisplayLabel(participant?.name, participant?.email);
};

const getApplicationPreviewMessage = (application) => (
  application?.latest_application_message || application?.message || '-'
);

const getApplicationConversation = (application) => {
  if (Array.isArray(application?.application_messages) && application.application_messages.length > 0) {
    return application.application_messages;
  }

  if (!application?.message) {
    return [];
  }

  return [{
    id: `${application.id}-initial-message`,
    body: application.message,
    sender_role: 'tenant',
    created_at: application.created_at,
  }];
};

const useClientPagination = (items, pageSize = ADMIN_TAB_PAGE_SIZE) => {
  const [page, setPage] = useState(1);

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pageItems = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return items.slice(startIndex, startIndex + pageSize);
  }, [items, page, pageSize]);

  return {
    page,
    setPage,
    pageSize,
    totalItems,
    totalPages,
    pageItems,
    rangeStart: totalItems === 0 ? 0 : ((page - 1) * pageSize) + 1,
    rangeEnd: totalItems === 0 ? 0 : Math.min(page * pageSize, totalItems),
  };
};

const AdminTabPagination = ({ pagination, itemLabel }) => {
  if (!pagination || pagination.totalItems <= pagination.pageSize) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 border-t px-6 py-4 text-sm sm:flex-row sm:items-center sm:justify-between">
      <span className="text-muted-foreground">
        Showing {pagination.rangeStart}-{pagination.rangeEnd} of {pagination.totalItems} {itemLabel}
      </span>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button
          className="w-full sm:w-auto"
          size="sm"
          variant="outline"
          onClick={() => pagination.setPage((current) => Math.max(1, current - 1))}
          disabled={pagination.page <= 1}
        >
          Previous
        </Button>
        <span className="text-muted-foreground">Page {pagination.page} of {pagination.totalPages}</span>
        <Button
          className="w-full sm:w-auto"
          size="sm"
          variant="outline"
          onClick={() => pagination.setPage((current) => Math.min(pagination.totalPages, current + 1))}
          disabled={pagination.page >= pagination.totalPages}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const VALID_TABS = new Set([
  'users', 'landlords', 'engagement', 'properties', 'assignments', 'applications',
  'contacts', 'issues', 'rent', 'document-delivery', 'audit', 'compliance',
  'activity', 'health-check-audit', 'email-logs', 'signing', 'market-rate-history', 'monitoring',
  'api-usage',
]);
const DEFAULT_TAB = 'users';

export const AdminDashboard = () => {
  useEffect(() => { document.title = 'Admin Dashboard - SIYAK'; }, []);
  const location = useLocation();
  const { user } = useAuth();
  const isMountedRef = useRef(false);
  const currentRoleRef = useRef(user?.role ?? null);
  const retryTimeoutRef = useRef(null);
  const dashboardErrorToastAtRef = useRef(0);
  const tabScrollRef = useRef(null);
  const hasScrolledTabsRef = useRef(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [issues, setIssues] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [rentSchedules, setRentSchedules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactReply, setContactReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [suggestingReply, setSuggestingReply] = useState(false);
  const [loadingContact, setLoadingContact] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState(new Set());
  const [selectedProperties, setSelectedProperties] = useState(new Set());
  const [selectedRentSchedules, setSelectedRentSchedules] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedLandlords, setSelectedLandlords] = useState(new Set());
  const [selectedAssignments, setSelectedAssignments] = useState(new Set());
  const [bulkDeletingContacts, setBulkDeletingContacts] = useState(false);
  const [bulkMarkingContactsRead, setBulkMarkingContactsRead] = useState(false);
  const [bulkDeletingIssues, setBulkDeletingIssues] = useState(false);
  const [bulkDeletingProperties, setBulkDeletingProperties] = useState(false);
  const [bulkDeletingRent, setBulkDeletingRent] = useState(false);
  const [bulkUnmarkingRent, setBulkUnmarkingRent] = useState(false);
  const [bulkApprovingUsers, setBulkApprovingUsers] = useState(false);
  const [bulkDeletingUsers, setBulkDeletingUsers] = useState(false);
  const [bulkApprovingLandlords, setBulkApprovingLandlords] = useState(false);
  const [bulkDeletingLandlords, setBulkDeletingLandlords] = useState(false);
  const [bulkDeletingAssignments, setBulkDeletingAssignments] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const repliesEndRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = VALID_TABS.has(searchParams.get('tab')) ? searchParams.get('tab') : DEFAULT_TAB;
  const [tabLastViewed, setTabLastViewed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('adminTabLastViewed') || '{}'); }
    catch { return {}; }
  });

  const handleTabChange = useCallback((tab) => {
    if (!VALID_TABS.has(tab)) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
    const now = new Date().toISOString();
    setTabLastViewed(prev => {
      const next = { ...prev, [tab]: now };
      try { localStorage.setItem('adminTabLastViewed', JSON.stringify(next)); } catch {}
      return next;
    });
  }, [setSearchParams, setTabLastViewed]);

  // Mark the initially-active tab as viewed on mount (badge tracking only — no URL push)
  useEffect(() => {
    const now = new Date().toISOString();
    setTabLastViewed(prev => {
      const next = { ...prev, [activeTab]: now };
      try { localStorage.setItem('adminTabLastViewed', JSON.stringify(next)); } catch {}
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countNewSince = (items, tabKey, dateField = 'created_at') => {
    const lastViewed = tabLastViewed[tabKey];
    if (!lastViewed) return items.length;
    const threshold = new Date(lastViewed).getTime();
    return items.filter(item => {
      const d = item[dateField];
      return d && new Date(d).getTime() > threshold;
    }).length;
  };

  // Renders a tab count badge combining new-since-last-view and actionable counts.
  // Only the non-zero parts are shown; returns null when both are zero.
  const tabBadge = (newCount, actionCount = 0) => {
    if (newCount === 0 && actionCount === 0) return null;
    const parts = [];
    if (newCount > 0) parts.push(`${newCount} new`);
    if (actionCount > 0) parts.push(`${actionCount}`);
    return <span className="ml-1 text-xs text-muted-foreground">({parts.join(' · ')})</span>;
  };

  useEffect(() => {
    const hashTab = location.hash?.replace('#', '');
    if (!hashTab) return;
    if (VALID_TABS.has(hashTab)) {
      handleTabChange(hashTab);
    }
  }, [location.hash, handleTabChange]);

  useEffect(() => {
    if (!hasScrolledTabsRef.current) {
      hasScrolledTabsRef.current = true;
      return undefined;
    }

    if (!tabScrollRef.current) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      scrollToElementWithOffset(tabScrollRef.current);
    }, 100);

    return () => window.clearTimeout(timerId);
  }, [activeTab]);
  
  // Phase 1: refresh + timestamp
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [marketRateRevision, setMarketRateRevision] = useState(0);
  const [, setTickCounter] = useState(0); // forces re-render every 30s for relative time

  // Phase 2: new search/filter state
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState('all');
  const [contactSearch, setContactSearch] = useState('');
  const [contactReadFilter, setContactReadFilter] = useState('all');
  const [landlordSearch, setLandlordSearch] = useState('');
  const [landlordStatusFilter, setLandlordStatusFilter] = useState('all');
  const [landlordTypeFilter, setLandlordTypeFilter] = useState('all');
  const [propertyStatusFilter, setPropertyStatusFilter] = useState('all');

  // Phase 3: new data state
  const [applications, setApplications] = useState([]);
  const [applicationSearch, setApplicationSearch] = useState('');
  const [applicationStatusFilter, setApplicationStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityRecencyFilter, setActivityRecencyFilter] = useState('all');
  const [lifecycleMonitoring, setLifecycleMonitoring] = useState(null);
  const [deliveryStats, setDeliveryStats] = useState(null);
  const [storageMonitoring, setStorageMonitoring] = useState(null);
  const [healthCheckAudit, setHealthCheckAudit] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsTotal, setEmailLogsTotal] = useState(0);
  const [signingVersions, setSigningVersions] = useState([]);
  const [emailLogSearch, setEmailLogSearch] = useState('');
  const [emailLogStatusFilter, setEmailLogStatusFilter] = useState('all');
  const [emailLogTypeFilter, setEmailLogTypeFilter] = useState('all');
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailProviderSettings, setEmailProviderSettings] = useState(null);
  const [emailProviderSettingsLoading, setEmailProviderSettingsLoading] = useState(false);
  const [emailProviderUpdating, setEmailProviderUpdating] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderRulesOpen, setReminderRulesOpen] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [viewingBackups, setViewingBackups] = useState(false);
  const [backupsData, setBackupsData] = useState([]);

  // Phase 4: export loading state
  const [exportingIssues, setExportingIssues] = useState(false);
  const [exportingRent, setExportingRent] = useState(false);
  const [exportingContacts, setExportingContacts] = useState(false);

  // API Usage tab
  const [apiUsage, setApiUsage] = useState(null);
  const [apiUsageLoading, setApiUsageLoading] = useState(false);
  const [apiUsageSearch, setApiUsageSearch] = useState('');
  const [apiUsageServiceFilter, setApiUsageServiceFilter] = useState('all');

  // Search/filters
  const [issueSearch, setIssueSearch] = useState('');
  const [issueStatusFilter, setIssueStatusFilter] = useState('all');
  const [issuePriorityFilter, setIssuePriorityFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [propertySearch, setPropertySearch] = useState('');
  const [propertyLandlordFilter, setPropertyLandlordFilter] = useState('all');
  const [rentSearch, setRentSearch] = useState('');
  const [rentStatusFilter, setRentStatusFilter] = useState('all');
  
  // Modals
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueNotes, setIssueNotes] = useState('');
  const [updatingIssue, setUpdatingIssue] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({});
  const [editProperty, setEditProperty] = useState(null);
  const [editPropertyForm, setEditPropertyForm] = useState({});
  const [editRent, setEditRent] = useState(null);
  const [editRentForm, setEditRentForm] = useState({});
  const [editAssignment, setEditAssignment] = useState(null);
  const [editAssignmentForm, setEditAssignmentForm] = useState({});
  const [resetPwUser, setResetPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [adminUserErrors, setAdminUserErrors] = useState({});

  // Create modals
  const [createUser, setCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ name: '', email: '', password: '', role: 'tenant', phone: '', ownership_type: 'individual', company_name: '', company_registration: '' });
  const [createProperty, setCreateProperty] = useState(false);
  const [createPropertyForm, setCreatePropertyForm] = useState({ title: '', address: '', postcode: '', rent: 0, floors: '', property_type: 'flat', landlord_id: '', is_occupied: false });
  const [createIssue, setCreateIssue] = useState(false);
  const [createIssueForm, setCreateIssueForm] = useState({ property_id: '', description: '', category: DEFAULT_ISSUE_CATEGORY, sub_category: '', custom_title: '', priority: 'medium' });
  const [createRent, setCreateRent] = useState(false);
  const [createRentForm, setCreateRentForm] = useState({ property_id: '', tenant_id: '', rent_amount: 0, due_day_of_month: 1, payment_method: 'bank_transfer', notes: '' });
  const [createAssignment, setCreateAssignment] = useState(false);
  const [createAssignmentForm, setCreateAssignmentForm] = useState({ tenant_email: '', property_id: '', start_date: '', notes: '', send_invite_email: true });
  const [tenants, setTenants] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    title: '',
    description: '',
    actionLabel: 'Delete',
    onConfirm: null,
    isDestructive: true,
  });
  const createIssueSubCategoryOptions = ISSUE_CATEGORIES[createIssueForm.category] || [];
  const SelectedAdminCategoryIcon = CATEGORY_ICONS[createIssueForm.category];

  const updateCreateIssueDetails = (updates) => {
    setCreateIssueForm((current) => {
      const currentTemplate = getIssueDescriptionTemplate(current);
      const next = { ...current, ...updates };
      const nextTemplate = getIssueDescriptionTemplate(next);

      if (!current.description.trim() || current.description === currentTemplate) {
        return {
          ...next,
          description: nextTemplate,
        };
      }

      return next;
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const issueId = params.get('issueId');
    if (!issueId) return;
    handleTabChange('issues');
    const issue = issues.find((item) => item.id === issueId);
    if (issue) {
      setSelectedIssue(issue);
      setIssueNotes(issue.admin_notes || '');
    }
  }, [issues, location.search, handleTabChange]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const applicationId = params.get('applicationId');
    if (!applicationId) return;
    handleTabChange('applications');
    const application = applications.find((item) => item.id === applicationId);
    if (application) {
      setSelectedApplication(application);
    }
  }, [applications, location.search, handleTabChange]);

  const findExistingUserByEmail = useCallback((email) => {
    const normalizedEmail = normalizeEmailValue(email);
    if (!normalizedEmail) return null;
    return users.find((candidate) => normalizeEmailValue(candidate.email || '') === normalizedEmail) || null;
  }, [users]);

  const getActiveAssignmentsForProperty = useCallback((propertyId) => {
    if (!propertyId) return [];
    const seenTenantIds = new Set();
    return assignments.filter((assignment) => {
      if (assignment.property_id !== propertyId || assignment.status !== 'active' || !assignment.tenant_id) {
        return false;
      }
      if (seenTenantIds.has(assignment.tenant_id)) {
        return false;
      }
      seenTenantIds.add(assignment.tenant_id);
      return true;
    });
  }, [assignments]);

  // === Client-side filtered lists ===
  const filteredUsers = useMemo(() => {
    let list = users;
    if (userSearch) {
      const q = userSearch.toLowerCase();
      list = list.filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
    }
    if (userRoleFilter !== 'all') list = list.filter(u => u.role === userRoleFilter);
    if (userStatusFilter === 'active') list = list.filter(u => u.is_active !== false && u.is_approved);
    else if (userStatusFilter === 'inactive') list = list.filter(u => u.is_active === false);
    else if (userStatusFilter === 'pending') list = list.filter(u => !u.is_approved);
    return list;
  }, [users, userSearch, userRoleFilter, userStatusFilter]);

  const filteredLandlords = useMemo(() => {
    let list = landlords;
    if (landlordSearch) {
      const q = landlordSearch.toLowerCase();
      list = list.filter(l => l.name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q));
    }
    if (landlordStatusFilter === 'active') list = list.filter(l => l.is_active !== false && l.is_approved);
    else if (landlordStatusFilter === 'deactivated') list = list.filter(l => l.is_active === false);
    else if (landlordStatusFilter === 'pending') list = list.filter(l => !l.is_approved);
    if (landlordTypeFilter === 'company') list = list.filter(l => l.ownership_type === 'company');
    else if (landlordTypeFilter === 'individual') list = list.filter(l => !l.ownership_type || l.ownership_type === 'individual');
    return list;
  }, [landlords, landlordSearch, landlordStatusFilter, landlordTypeFilter]);

  const filteredProperties = useMemo(() => {
    let list = properties;
    if (propertySearch) {
      const q = propertySearch.toLowerCase();
      list = list.filter(p => p.address?.toLowerCase().includes(q) || p.title?.toLowerCase().includes(q));
    }
    if (propertyLandlordFilter !== 'all') list = list.filter(p => p.landlord_id === propertyLandlordFilter);
    if (propertyStatusFilter === 'available') list = list.filter(p => !p.is_occupied);
    else if (propertyStatusFilter === 'rented') list = list.filter(p => p.is_occupied);
    return list;
  }, [properties, propertySearch, propertyLandlordFilter, propertyStatusFilter]);

  const filteredRent = useMemo(() => {
    let list = rentSchedules;

    if (rentSearch) {
      const q = rentSearch.toLowerCase();
      list = list.filter((r) => (
        r.property_title?.toLowerCase().includes(q)
        || r.tenant_name?.toLowerCase().includes(q)
        || r.next_due_date?.toLowerCase().includes(q)
        || r.status?.toLowerCase().includes(q)
        || String(r.rent_amount ?? '').toLowerCase().includes(q)
      ));
    }

    if (rentStatusFilter !== 'all') {
      list = list.filter((r) => r.status === rentStatusFilter);
    }

    return list;
  }, [rentSchedules, rentSearch, rentStatusFilter]);

  const filteredAssignments = useMemo(() => {
    let list = assignments;
    if (assignmentSearch) {
      const q = assignmentSearch.toLowerCase();
      list = list.filter(a =>
        getAssignmentTenantSearchText(a).includes(q) ||
        a.property_title?.toLowerCase().includes(q) ||
        a.property_address?.toLowerCase().includes(q)
      );
    }
    if (assignmentStatusFilter !== 'all') list = list.filter(a => a.status === assignmentStatusFilter);
    return list;
  }, [assignments, assignmentSearch, assignmentStatusFilter]);

  const filteredContacts = useMemo(() => {
    let list = contacts;
    if (contactSearch) {
      const q = contactSearch.toLowerCase();
      list = list.filter(c => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q));
    }
    if (contactReadFilter === 'unread') list = list.filter(c => !c.is_read);
    else if (contactReadFilter === 'read') list = list.filter(c => c.is_read);
    return list;
  }, [contacts, contactSearch, contactReadFilter]);

  const getApplicationDecisionStatus = (application) => application?.decision_status || application?.status || 'pending';

  const filteredApplications = useMemo(() => {
    let list = applications;
    if (applicationSearch) {
      const q = applicationSearch.toLowerCase();
      list = list.filter(a =>
        a.tenant_name?.toLowerCase().includes(q) ||
        a.tenant_email?.toLowerCase().includes(q) ||
        a.property_title?.toLowerCase().includes(q) ||
        getApplicationPreviewMessage(a).toLowerCase().includes(q)
      );
    }
    if (applicationStatusFilter !== 'all') list = list.filter(a => getApplicationDecisionStatus(a) === applicationStatusFilter);
    return list;
  }, [applications, applicationSearch, applicationStatusFilter]);

  const filteredActivityLogs = useMemo(() => {
    let list = activityLogs;
    if (activitySearch) {
      const q = activitySearch.toLowerCase();
      list = list.filter(l =>
        l.user_name?.toLowerCase().includes(q) ||
        l.user_email?.toLowerCase().includes(q) ||
        l.action_type?.toLowerCase().includes(q)
      );
    }
    if (activityRecencyFilter !== 'all') {
      const days = activityRecencyFilter === '7d' ? 7 : 30;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      list = list.filter(l => l.created_at && new Date(l.created_at) >= cutoff);
    }
    return list;
  }, [activityLogs, activitySearch, activityRecencyFilter]);

  const filteredEmailLogs = useMemo(() => {
    let list = emailLogs;
    if (emailLogSearch) {
      const q = emailLogSearch.toLowerCase();
      list = list.filter(l =>
        l.to_email?.toLowerCase().includes(q) ||
        l.subject?.toLowerCase().includes(q) ||
        l.from_email?.toLowerCase().includes(q) ||
        l.email_type?.toLowerCase().includes(q)
      );
    }
    if (emailLogStatusFilter !== 'all') list = list.filter(l => l.status === emailLogStatusFilter);
    if (emailLogTypeFilter !== 'all') list = list.filter(l => l.email_type === emailLogTypeFilter);
    // Sort latest first using sent_at with created_at as fallback
    list = [...list].sort((a, b) => {
      const ta = new Date(a.sent_at || a.created_at || 0).getTime();
      const tb = new Date(b.sent_at || b.created_at || 0).getTime();
      return tb - ta;
    });
    return list;
  }, [emailLogs, emailLogSearch, emailLogStatusFilter, emailLogTypeFilter]);

  const filteredApiUsageLogs = useMemo(() => {
    let list = apiUsage?.logs?.items || [];
    if (apiUsageServiceFilter !== 'all') {
      list = list.filter(l => l.service === apiUsageServiceFilter);
    }
    if (apiUsageSearch) {
      const q = apiUsageSearch.toLowerCase();
      list = list.filter(l =>
        l.operation?.toLowerCase().includes(q) ||
        l.triggered_by_email?.toLowerCase().includes(q) ||
        l.location?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [apiUsage, apiUsageSearch, apiUsageServiceFilter]);

  const issuesPagination = useClientPagination(issues);
  const propertiesPagination = useClientPagination(filteredProperties);
  const rentPagination = useClientPagination(filteredRent);
  const usersPagination = useClientPagination(filteredUsers);
  const landlordsPagination = useClientPagination(filteredLandlords);
  const assignmentsPagination = useClientPagination(filteredAssignments);
  const contactsPagination = useClientPagination(filteredContacts);
  const applicationsPagination = useClientPagination(filteredApplications);
  const activityPagination = useClientPagination(filteredActivityLogs);
  const healthCheckAuditPagination = useClientPagination(healthCheckAudit);
  const emailLogsPagination = useClientPagination(filteredEmailLogs);
  const apiUsageLogsPagination = useClientPagination(filteredApiUsageLogs);
  const paginatedApiUsageLogs = apiUsageLogsPagination.pageItems;

  const paginatedIssues = issuesPagination.pageItems;
  const paginatedProperties = propertiesPagination.pageItems;
  const paginatedRent = rentPagination.pageItems;
  const paginatedUsers = usersPagination.pageItems;
  const paginatedLandlords = landlordsPagination.pageItems;
  const paginatedAssignments = assignmentsPagination.pageItems;
  const paginatedContacts = contactsPagination.pageItems;
  const paginatedApplications = applicationsPagination.pageItems;
  const paginatedActivityLogs = activityPagination.pageItems;
  const paginatedHealthCheckAudit = healthCheckAuditPagination.pageItems;
  const paginatedEmailLogs = emailLogsPagination.pageItems;

  const currentWorkingProviderLabel = useMemo(() => {
    const provider = emailProviderSettings?.current_working_provider;
    if (!provider) return 'Unavailable';
    if (provider === 'mocked') return 'Mocked';
    return provider === 'brevo' ? 'Brevo' : 'Resend';
  }, [emailProviderSettings]);

  const preferredEmailProviderLabel = useMemo(() => (
    emailProviderSettings?.preferred_email_provider === 'brevo' ? 'Brevo' : 'Resend'
  ), [emailProviderSettings]);

  const areAllVisibleIssuesSelected = paginatedIssues.length > 0 && paginatedIssues.every((issue) => selectedIssues.has(issue.id));
  const areAllVisiblePropertiesSelected = paginatedProperties.length > 0 && paginatedProperties.every((property) => selectedProperties.has(property.id));
  const areAllVisibleRentSelected = paginatedRent.length > 0 && paginatedRent.every((rent) => selectedRentSchedules.has(rent.id));
  const areAllVisibleUsersSelected = paginatedUsers.length > 0 && paginatedUsers.every((userRow) => selectedUsers.has(userRow.id));
  const areAllVisibleLandlordsSelected = paginatedLandlords.length > 0 && paginatedLandlords.every((landlord) => selectedLandlords.has(landlord.id));
  const areAllVisibleAssignmentsSelected = paginatedAssignments.length > 0 && paginatedAssignments.every((assignment) => selectedAssignments.has(assignment.id));
  const areAllVisibleContactsSelected = paginatedContacts.length > 0 && paginatedContacts.every((contact) => selectedContacts.has(contact.id));

  const toggleIssueSelection = (issueId) => {
    setSelectedIssues((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  };

  const handleToggleSelectAllIssues = () => {
    setSelectedIssues((prev) => {
      const next = new Set(prev);
      if (areAllVisibleIssuesSelected) {
        paginatedIssues.forEach((issue) => next.delete(issue.id));
        return next;
      }
      paginatedIssues.forEach((issue) => next.add(issue.id));
      return next;
    });
  };

  const handleBulkDeleteIssues = () => {
    const idsToDelete = Array.from(selectedIssues);
    if (idsToDelete.length === 0) return;

    openDeleteConfirm({
      title: `Delete ${idsToDelete.length} issue(s)?`,
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        setBulkDeletingIssues(true);
        try {
          const results = await Promise.allSettled(idsToDelete.map((issueId) => axios.delete(`${API_URL}/api/issues/${issueId}`)));
          const successCount = results.filter((result) => result.status === 'fulfilled').length;
          if (successCount > 0) {
            toast.success(`${successCount} issue(s) deleted`);
            if (selectedIssue && idsToDelete.includes(selectedIssue.id)) {
              setSelectedIssue(null);
            }
            setSelectedIssues(new Set());
            fetchData();
          }
          if (successCount !== idsToDelete.length) {
            toast.error('Some selected issues could not be deleted');
          }
        } catch {
          toast.error('Failed to delete selected issues');
        } finally {
          setBulkDeletingIssues(false);
        }
      },
    });
  };

  const togglePropertySelection = (propertyId) => {
    setSelectedProperties((prev) => {
      const next = new Set(prev);
      if (next.has(propertyId)) next.delete(propertyId);
      else next.add(propertyId);
      return next;
    });
  };

  const handleToggleSelectAllProperties = () => {
    setSelectedProperties((prev) => {
      const next = new Set(prev);
      if (areAllVisiblePropertiesSelected) {
        paginatedProperties.forEach((property) => next.delete(property.id));
        return next;
      }
      paginatedProperties.forEach((property) => next.add(property.id));
      return next;
    });
  };

  const handleBulkDeleteProperties = () => {
    const idsToDelete = Array.from(selectedProperties);
    if (idsToDelete.length === 0) return;

    openDeleteConfirm({
      title: `Delete ${idsToDelete.length} propert${idsToDelete.length === 1 ? 'y' : 'ies'}?`,
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        setBulkDeletingProperties(true);
        try {
          const results = await Promise.allSettled(idsToDelete.map((propertyId) => axios.delete(`${API_URL}/api/properties/${propertyId}`)));
          const successCount = results.filter((result) => result.status === 'fulfilled').length;
          if (successCount > 0) {
            toast.success(`${successCount} propert${successCount === 1 ? 'y' : 'ies'} deleted`);
            setSelectedProperties(new Set());
            fetchData();
          }
          if (successCount !== idsToDelete.length) {
            toast.error('Some selected properties could not be deleted');
          }
        } catch {
          toast.error('Failed to delete selected properties');
        } finally {
          setBulkDeletingProperties(false);
        }
      },
    });
  };

  const toggleRentSelection = (rentId) => {
    setSelectedRentSchedules((prev) => {
      const next = new Set(prev);
      if (next.has(rentId)) next.delete(rentId);
      else next.add(rentId);
      return next;
    });
  };

  const handleToggleSelectAllRent = () => {
    setSelectedRentSchedules((prev) => {
      const next = new Set(prev);
      if (areAllVisibleRentSelected) {
        paginatedRent.forEach((rent) => next.delete(rent.id));
        return next;
      }
      paginatedRent.forEach((rent) => next.add(rent.id));
      return next;
    });
  };

  const handleBulkDeleteRent = () => {
    const idsToDelete = Array.from(selectedRentSchedules);
    if (idsToDelete.length === 0) return;

    openDeleteConfirm({
      title: `Delete ${idsToDelete.length} rent schedule(s)?`,
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        setBulkDeletingRent(true);
        try {
          const results = await Promise.allSettled(idsToDelete.map((rentId) => axios.delete(`${API_URL}/api/rent/${rentId}`)));
          const successCount = results.filter((result) => result.status === 'fulfilled').length;
          if (successCount > 0) {
            toast.success(`${successCount} rent schedule(s) deleted`);
            setSelectedRentSchedules(new Set());
            fetchData();
          }
          if (successCount !== idsToDelete.length) {
            toast.error('Some selected rent schedules could not be deleted');
          }
        } catch {
          toast.error('Failed to delete selected rent schedules');
        } finally {
          setBulkDeletingRent(false);
        }
      },
    });
  };

  const handleBulkUnmarkRent = () => {
    const idsToUnmark = Array.from(selectedRentSchedules);
    if (idsToUnmark.length === 0) return;

    openDeleteConfirm({
      title: `Unmark ${idsToUnmark.length} rent schedule(s) as paid?`,
      description: 'This will remove the payment record and generate new receipts for the tenant.',
      actionLabel: 'Unmark',
      isDestructive: false,
      onConfirm: async () => {
        setBulkUnmarkingRent(true);
        try {
          const results = await Promise.allSettled(idsToUnmark.map((rentId) => axios.delete(`${API_URL}/api/rent/${rentId}/unmark-paid`)));
          const successCount = results.filter((result) => result.status === 'fulfilled').length;
          if (successCount > 0) {
            toast.success(`${successCount} rent schedule(s) unmarked`);
            setSelectedRentSchedules(new Set());
            fetchData();
          }
          if (successCount !== idsToUnmark.length) {
            toast.error('Some selected rent schedules could not be unmarked');
          }
        } catch {
          toast.error('Failed to unmark selected rent schedules');
        } finally {
          setBulkUnmarkingRent(false);
        }
      },
    });
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleToggleSelectAllUsers = () => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (areAllVisibleUsersSelected) {
        paginatedUsers.forEach((userRow) => next.delete(userRow.id));
        return next;
      }
      paginatedUsers.forEach((userRow) => next.add(userRow.id));
      return next;
    });
  };

  const toggleLandlordSelection = (landlordId) => {
    setSelectedLandlords((prev) => {
      const next = new Set(prev);
      if (next.has(landlordId)) next.delete(landlordId);
      else next.add(landlordId);
      return next;
    });
  };

  const handleToggleSelectAllLandlords = () => {
    setSelectedLandlords((prev) => {
      const next = new Set(prev);
      if (areAllVisibleLandlordsSelected) {
        paginatedLandlords.forEach((landlord) => next.delete(landlord.id));
        return next;
      }
      paginatedLandlords.forEach((landlord) => next.add(landlord.id));
      return next;
    });
  };

  const handleBulkApproveUsers = async (fromLandlordsTab = false) => {
    const selectedSet = fromLandlordsTab ? selectedLandlords : selectedUsers;
    const sourceList = fromLandlordsTab ? filteredLandlords : filteredUsers;
    const idsToApprove = sourceList
      .filter((row) => selectedSet.has(row.id) && row.role === 'landlord' && !row.is_approved && row.is_email_verified)
      .map((row) => row.id);

    if (idsToApprove.length === 0) {
      toast.error('No approvable landlords selected');
      return;
    }

    if (fromLandlordsTab) setBulkApprovingLandlords(true);
    else setBulkApprovingUsers(true);
    try {
      const results = await Promise.allSettled(
        idsToApprove.map((userId) => axios.put(`${API_URL}/api/admin/users/${userId}`, { is_approved: true }))
      );
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      if (successCount > 0) {
        toast.success(`${successCount} landlord account(s) approved`);
        if (fromLandlordsTab) setSelectedLandlords(new Set());
        else setSelectedUsers(new Set());
        fetchData();
      }
      if (successCount !== idsToApprove.length) {
        toast.error('Some selected landlords could not be approved');
      }
    } catch {
      toast.error('Failed to approve selected landlords');
    } finally {
      if (fromLandlordsTab) setBulkApprovingLandlords(false);
      else setBulkApprovingUsers(false);
    }
  };

  const handleBulkDeactivateUsers = (fromLandlordsTab = false) => {
    const selectedSet = fromLandlordsTab ? selectedLandlords : selectedUsers;
    const idsToDeactivate = Array.from(selectedSet);
    if (idsToDeactivate.length === 0) return;

    openDeleteConfirm({
      title: `Deactivate ${idsToDeactivate.length} user(s)?`,
      description: 'This removes portal access without deleting account records or operational history.',
      onConfirm: async () => {
        if (fromLandlordsTab) setBulkDeletingLandlords(true);
        else setBulkDeletingUsers(true);
        try {
          const results = await Promise.allSettled(idsToDeactivate.map((userId) => axios.put(`${API_URL}/api/admin/users/${userId}`, { is_active: false })));
          const successCount = results.filter((result) => result.status === 'fulfilled').length;
          if (successCount > 0) {
            toast.success(`${successCount} user(s) deactivated`);
            if (editUser && idsToDeactivate.includes(editUser.id)) {
              setEditUser(null);
            }
            if (resetPwUser && idsToDeactivate.includes(resetPwUser.id)) {
              setResetPwUser(null);
            }
            if (fromLandlordsTab) setSelectedLandlords(new Set());
            else setSelectedUsers(new Set());
            fetchData();
          }
          if (successCount !== idsToDeactivate.length) {
            toast.error('Some selected users could not be deactivated');
          }
        } catch {
          toast.error('Failed to deactivate selected users');
        } finally {
          if (fromLandlordsTab) setBulkDeletingLandlords(false);
          else setBulkDeletingUsers(false);
        }
      },
    });
  };

  const toggleAssignmentSelection = (assignmentId) => {
    setSelectedAssignments((prev) => {
      const next = new Set(prev);
      if (next.has(assignmentId)) next.delete(assignmentId);
      else next.add(assignmentId);
      return next;
    });
  };

  const handleToggleSelectAllAssignments = () => {
    setSelectedAssignments((prev) => {
      const next = new Set(prev);
      if (areAllVisibleAssignmentsSelected) {
        paginatedAssignments.forEach((assignment) => next.delete(assignment.id));
        return next;
      }
      paginatedAssignments.forEach((assignment) => next.add(assignment.id));
      return next;
    });
  };

  const handleBulkDeleteAssignments = () => {
    const idsToDelete = Array.from(selectedAssignments);
    if (idsToDelete.length === 0) return;

    // Pre-capture: properties that may become fully unoccupied
    const deletingSet = new Set(idsToDelete);
    const activeCountByProperty = {};
    assignments.forEach(a => {
      if (a.status === 'active') activeCountByProperty[a.property_id] = (activeCountByProperty[a.property_id] || 0) + 1;
    });
    const deletingActiveByProperty = {};
    assignments.filter(a => deletingSet.has(a.id) && a.status === 'active').forEach(a => {
      deletingActiveByProperty[a.property_id] = (deletingActiveByProperty[a.property_id] || 0) + 1;
    });
    const clearingProperties = properties.filter(prop =>
      prop.is_occupied &&
      (activeCountByProperty[prop.id] || 0) > 0 &&
      (deletingActiveByProperty[prop.id] || 0) >= (activeCountByProperty[prop.id] || 0)
    );

    openDeleteConfirm({
      title: `Delete ${idsToDelete.length} assignment(s)?`,
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        setBulkDeletingAssignments(true);
        try {
          const results = await Promise.allSettled(
            idsToDelete.map((assignmentId) => axios.delete(`${API_URL}/api/tenant-assignments/${assignmentId}`))
          );
          const successCount = results.filter((result) => result.status === 'fulfilled').length;
          if (successCount > 0) {
            toast.success(`${successCount} assignment(s) deleted`);
            setSelectedAssignments(new Set());
            fetchData();
            if (clearingProperties.length === 1) {
              const prop = clearingProperties[0];
              toast.info(`"${prop.title}" may now be unoccupied. Update occupancy?`, {
                action: { label: 'Mark Unoccupied', onClick: () => suggestOccupancy(prop.id, false) },
                duration: 8000,
              });
            } else if (clearingProperties.length > 1) {
              toast.info(`${clearingProperties.length} properties may now be unoccupied.`, {
                action: {
                  label: 'Mark All Unoccupied',
                  onClick: async () => {
                    await Promise.all(clearingProperties.map(p => axios.patch(`${API_URL}/api/properties/${p.id}`, { is_occupied: false })));
                    fetchData();
                    toast.success(`${clearingProperties.length} properties marked as unoccupied`);
                  },
                },
                duration: 8000,
              });
            }
          }
          if (successCount !== idsToDelete.length) {
            toast.error('Some selected assignments could not be deleted');
          }
        } catch {
          toast.error('Failed to delete selected assignments');
        } finally {
          setBulkDeletingAssignments(false);
        }
      },
    });
  };

  const openDeleteConfirm = ({ title, description, actionLabel = 'Delete', onConfirm, isDestructive = true }) => {
    setDeleteConfirm({
      open: true,
      title,
      description,
      actionLabel,
      onConfirm,
      isDestructive,
    });
  };

  useEffect(() => {
    setSelectedIssues((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(issues.map((issue) => issue.id));
      const next = new Set(Array.from(prev).filter((issueId) => validIds.has(issueId)));
      return next.size === prev.size ? prev : next;
    });
  }, [issues]);

  useEffect(() => {
    setSelectedProperties((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(filteredProperties.map((property) => property.id));
      const next = new Set(Array.from(prev).filter((propertyId) => validIds.has(propertyId)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredProperties]);

  useEffect(() => {
    setSelectedRentSchedules((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(filteredRent.map((rent) => rent.id));
      const next = new Set(Array.from(prev).filter((rentId) => validIds.has(rentId)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredRent]);

  useEffect(() => {
    setSelectedUsers((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(filteredUsers.map((userRow) => userRow.id));
      const next = new Set(Array.from(prev).filter((userId) => validIds.has(userId)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredUsers]);

  useEffect(() => {
    setSelectedLandlords((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(filteredLandlords.map((landlord) => landlord.id));
      const next = new Set(Array.from(prev).filter((landlordId) => validIds.has(landlordId)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredLandlords]);

  useEffect(() => {
    setSelectedAssignments((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(filteredAssignments.map((assignment) => assignment.id));
      const next = new Set(Array.from(prev).filter((assignmentId) => validIds.has(assignmentId)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredAssignments]);


  const isExpectedAuthError = (error) => {
    const status = error?.response?.status;
    return status === 401 || status === 403;
  };

  const shouldShowDashboardErrorToast = () => {
    const now = Date.now();
    if (now - dashboardErrorToastAtRef.current < 10000) {
      return false;
    }
    dashboardErrorToastAtRef.current = now;
    return true;
  };

  useEffect(() => {
    currentRoleRef.current = user?.role ?? null;
  }, [user?.role]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  const fetchData = useCallback(async (retryCount = 0, isManualRefresh = false) => {
    if (!isMountedRef.current) {
      return;
    }

    if (currentRoleRef.current !== 'admin') {
      setLoading(false);
      return;
    }

    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/landlords`),
        axios.get(`${API_URL}/api/issues`),
        axios.get(`${API_URL}/api/notifications`),
        axios.get(`${API_URL}/api/contact`),
        axios.get(`${API_URL}/api/properties`),
        axios.get(`${API_URL}/api/rent`),
        axios.get(`${API_URL}/api/tenant-assignments`),
        axios.get(`${API_URL}/api/applications`),
        axios.get(`${API_URL}/api/activity-logs`, { params: { limit: 5000 } }),
        axios.get(`${API_URL}/api/admin/health-check-email-audit`, { params: { limit: 5000 } }),
        axios.get(`${API_URL}/api/admin/signing-overview`),
        axios.get(`${API_URL}/api/admin/lifecycle-monitoring`),
        axios.get(`${API_URL}/api/admin/document-delivery/stats`),
        axios.get(`${API_URL}/api/admin/storage-monitoring`),
        fetchApiUsage(),
      ]);

      const [statsRes, usersRes, landlordsRes, issuesRes, notificationsRes, contactsRes, propsRes, rentRes, assignRes, applicationsRes, activityRes, healthAuditRes, signingRes, lifecycleRes, deliveryStatsRes, storageMonitoringRes] = results;

      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data);
        setTenants(usersRes.value.data.filter(u => u.role === 'tenant'));
      }
      if (landlordsRes.status === 'fulfilled') setLandlords(landlordsRes.value.data);
      if (issuesRes.status === 'fulfilled') setIssues(unwrapApiList(issuesRes.value.data));
      if (notificationsRes.status === 'fulfilled') setNotifications(notificationsRes.value.data);
      if (contactsRes.status === 'fulfilled') setContacts(contactsRes.value.data);
      if (propsRes.status === 'fulfilled') setProperties(propsRes.value.data);
      if (rentRes.status === 'fulfilled') setRentSchedules(rentRes.value.data);
      if (assignRes.status === 'fulfilled') setAssignments(assignRes.value.data);
      if (applicationsRes.status === 'fulfilled') setApplications(applicationsRes.value.data);
      if (activityRes.status === 'fulfilled') setActivityLogs(activityRes.value.data);
      if (healthAuditRes.status === 'fulfilled') setHealthCheckAudit(healthAuditRes.value.data);
      if (signingRes.status === 'fulfilled') setSigningVersions(signingRes.value.data || []);
      if (lifecycleRes.status === 'fulfilled') setLifecycleMonitoring(lifecycleRes.value.data);
      if (deliveryStatsRes.status === 'fulfilled') setDeliveryStats(deliveryStatsRes.value.data);
      if (storageMonitoringRes.status === 'fulfilled') setStorageMonitoring(storageMonitoringRes.value.data);

      const rejected = results.filter((result) => result.status === 'rejected').map((result) => result.reason);
      const unexpectedFailures = rejected.filter((error) => !isExpectedAuthError(error));
      const onlyAuthFailures = rejected.length > 0 && unexpectedFailures.length === 0;

      if (onlyAuthFailures) {
        // Can be transient right after login while auth state settles.
        if (retryCount < 2) {
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }
          retryTimeoutRef.current = setTimeout(() => {
            retryTimeoutRef.current = null;
            if (!isMountedRef.current || currentRoleRef.current !== 'admin') {
              return;
            }
            fetchData(retryCount + 1, isManualRefresh);
          }, 350);
        }
        return;
      }

      if (unexpectedFailures.length > 0 && shouldShowDashboardErrorToast()) {
        toast.error('Some dashboard data failed to load');
      }

      if (isMountedRef.current) {
        setLastRefreshedAt(Date.now());
        setMarketRateRevision(r => r + 1);
      }
    } catch (error) {
      if (!isExpectedAuthError(error) && shouldShowDashboardErrorToast()) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      if (isMountedRef.current) {
        if (isManualRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmailLogs = useCallback(async ({ status, emailType, search } = {}) => {
    if (!isMountedRef.current) return;
    setEmailLogsLoading(true);
    try {
      const params = {};
      if (status && status !== 'all') params.status = status;
      if (emailType && emailType !== 'all') params.email_type = emailType;
      if (search) params.search = search;
      params.limit = 1000;
      const res = await axios.get(`${API_URL}/api/admin/email-logs`, { params });
      if (isMountedRef.current) {
        setEmailLogs(res.data.logs || []);
        setEmailLogsTotal(res.data.total || 0);
      }
    } catch (error) {
      if (!isExpectedAuthError(error)) {
        toast.error('Failed to load email logs');
      }
    } finally {
      if (isMountedRef.current) setEmailLogsLoading(false);
    }
  }, []);

  const fetchEmailProviderSettings = useCallback(async () => {
    if (!isMountedRef.current) return;
    setEmailProviderSettingsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/site-settings/email-provider`);
      if (isMountedRef.current) {
        setEmailProviderSettings(res.data);
      }
    } catch (error) {
      if (!isExpectedAuthError(error)) {
        toast.error('Failed to load email provider settings');
      }
    } finally {
      if (isMountedRef.current) setEmailProviderSettingsLoading(false);
    }
  }, []);

  const fetchApiUsage = useCallback(async () => {
    if (!isMountedRef.current) return;
    setApiUsageLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/api-usage`);
      if (isMountedRef.current) setApiUsage(res.data);
    } catch (error) {
      if (!isExpectedAuthError(error)) toast.error('Failed to load API usage data');
    } finally {
      if (isMountedRef.current) setApiUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (user?.role !== 'admin') {
      setLoading(false);
      return;
    }
    fetchData(0);
    fetchEmailLogs();
    fetchEmailProviderSettings();
  }, [user?.role, fetchData, fetchEmailLogs, fetchEmailProviderSettings]);

  const handlePreferredEmailProviderChange = async (checked) => {
    const nextProvider = checked ? 'brevo' : 'resend';
    const previousState = emailProviderSettings;

    setEmailProviderUpdating(true);
    setEmailProviderSettings((current) => current ? { ...current, preferred_email_provider: nextProvider } : current);
    try {
      const res = await axios.patch(`${API_URL}/api/admin/site-settings/email-provider`, {
        preferred_email_provider: nextProvider,
      });
      if (isMountedRef.current) {
        setEmailProviderSettings(res.data);
      }
      toast.success(`${nextProvider === 'brevo' ? 'Brevo' : 'Resend'} is now the preferred email provider`);
    } catch (error) {
      if (isMountedRef.current) {
        setEmailProviderSettings(previousState);
      }
      toast.error(error.response?.data?.detail || 'Failed to update preferred email provider');
    } finally {
      if (isMountedRef.current) setEmailProviderUpdating(false);
    }
  };

  // Tick every 30s so the "Last refreshed" text stays current without re-fetching
  useEffect(() => {
    const interval = setInterval(() => setTickCounter(n => n + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // === Issue search (server-side) ===
  const handleSearchIssues = useCallback(async (searchValue = '') => {
    try {
      const params = new URLSearchParams();
      if (searchValue) params.append('search', searchValue);
      if (issueStatusFilter !== 'all') params.append('status', issueStatusFilter);
      if (issuePriorityFilter !== 'all') params.append('priority', issuePriorityFilter);
      const response = await axios.get(`${API_URL}/api/issues?${params.toString()}`);
      setIssues(unwrapApiList(response.data));
    } catch (error) {
      const status = error?.response?.status;
      const isInitialEmptySearch = !searchValue
        && issueStatusFilter === 'all'
        && issuePriorityFilter === 'all'
        && issues.length === 0;
      if (status === 401 || (status === 403 && isInitialEmptySearch)) {
        setIssues([]);
        return;
      }
      toast.error('Failed to search issues');
    }
  }, [issueStatusFilter, issuePriorityFilter, issues.length]);

  useEffect(() => {
    if (!loading) handleSearchIssues(issueSearch);
  }, [loading, issueSearch, handleSearchIssues]);

  // === Handlers ===
  const handleToggleMoveIntent = async (userId, currentValue) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}`, { move_intent_enabled: !currentValue });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, move_intent_enabled: !currentValue } : u)));
      toast.success(`Move intent ${!currentValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update move intent');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}`, { is_approved: true });

      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_approved: true } : u)));
      setLandlords((prev) => prev.map((l) => (l.id === userId ? { ...l, is_approved: true } : l)));
      setStats((prev) => {
        if (!prev) return prev;
        const currentPending = prev?.users?.pending_landlords || 0;
        return {
          ...prev,
          users: {
            ...prev.users,
            pending_landlords: Math.max(0, currentPending - 1),
          },
        };
      });

      toast.success('User approved');
      await fetchData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleUpdateIssue = async (issueId, status, notes = null) => {
    setUpdatingIssue(true);
    try {
      const data = { status };
      if (notes) data.admin_notes = notes;
      await axios.put(`${API_URL}/api/issues/${issueId}`, data);
      toast.success('Issue updated');
      setSelectedIssue(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update issue');
    } finally {
      setUpdatingIssue(false);
    }
  };

  const handleMarkContactRead = async (contactId) => {
    try {
      await axios.put(`${API_URL}/api/contact/${contactId}/read`);
      fetchData();
    } catch (error) {
      console.error('Failed to mark contact as read:', error);
    }
  };

  const openContact = async (contactId) => {
    setLoadingContact(true);
    try {
      const res = await axios.get(`${API_URL}/api/contact/${contactId}`);
      setSelectedContact(res.data);
      // If unread, refresh list marker
      if (!res.data.is_read) fetchData();
    } catch (err) {
      console.error('Failed to load contact', err);
    } finally {
      setLoadingContact(false);
    }
  };

  useEffect(() => {
    // scroll to bottom when replies change
    if (selectedContact && repliesEndRef.current) {
      repliesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [selectedContact]);

  const handleReplyToContact = async () => {
    if (!contactReply?.trim()) return;
    setReplying(true);
    const now = new Date().toISOString();
    const optimisticReply = { text: contactReply, author: user?.name || 'Admin', timestamp: now };
    const prevReplies = selectedContact?.replies || [];
    // optimistic UI update
    setSelectedContact(prev => ({ ...prev, replies: [...(prev.replies || []), optimisticReply], is_read: true }));
    setContactReply('');
    try {
      await axios.post(`${API_URL}/api/contact/${selectedContact.id}/reply`, { text: optimisticReply.text });
      toast.success('Reply sent');
      fetchData();
    } catch (err) {
      // revert optimistic update
      setSelectedContact(prev => ({ ...prev, replies: prevReplies }));
      toast.error('Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleSuggestContactReply = async () => {
    if (!selectedContact?.id) return;
    setSuggestingReply(true);
    try {
      const res = await axios.post(`${API_URL}/api/contact/${selectedContact.id}/suggest-reply`, {}, { withCredentials: true });
      setContactReply(res.data.suggestion || '');
    } catch (err) {
      toast.error('Could not generate a suggestion right now');
    } finally {
      setSuggestingReply(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    openDeleteConfirm({
      title: 'Delete Message?',
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/contact/${contactId}`);
          toast.success('Contact deleted');
          setSelectedContacts((prev) => {
            if (!prev.has(contactId)) return prev;
            const next = new Set(prev);
            next.delete(contactId);
            return next;
          });
          setSelectedContact(null);
          fetchData();
        } catch (err) {
          toast.error('Failed to delete contact');
        }
      },
    });
  };

  const handleDeleteProperty = async (propertyId) => {
    openDeleteConfirm({
      title: 'Delete Property?',
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/properties/${propertyId}`);
          toast.success('Property deleted');
          fetchData();
        } catch (err) {
          const msg = err.response?.data?.detail || 'Failed to delete property';
          toast.error(msg);
        }
      },
    });
  };

  const handleDeleteRent = async (rentId) => {
    openDeleteConfirm({
      title: 'Delete Rent Schedule?',
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/rent/${rentId}`);
          toast.success('Rent schedule deleted');
          fetchData();
        } catch (err) {
          const msg = err.response?.data?.detail || 'Failed to delete rent schedule';
          toast.error(msg);
        }
      },
    });
  };

  const handleUnmarkRent = async (rentId) => {
    openDeleteConfirm({
      title: 'Unmark payment?',
      description: 'This will mark the rent schedule as unpaid and allow payment status changes again.',
      actionLabel: 'Unmark payment',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/rent/${rentId}/payment`);
          toast.success('Rent payment unmarked successfully');
          fetchData();
        } catch (err) {
          const msg = err.response?.data?.detail || 'Failed to unmark rent payment';
          toast.error(msg);
        }
      },
    });
  };

  const handleDeleteAssignment = async (assignmentId) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    const prop = properties.find(p => p.id === assignment?.property_id);
    const remainingActive = assignments.filter(a => a.property_id === prop?.id && a.id !== assignmentId && a.status === 'active');
    openDeleteConfirm({
      title: 'Delete Assignment?',
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/tenant-assignments/${assignmentId}`);
          toast.success('Assignment deleted');
          fetchData();
          if (prop?.is_occupied && remainingActive.length === 0) {
            toast.info(`"${prop.title}" may now be unoccupied. Update occupancy?`, {
              action: { label: 'Mark Unoccupied', onClick: () => suggestOccupancy(prop.id, false) },
              duration: 8000,
            });
          }
        } catch (err) {
          const msg = err.response?.data?.detail || 'Failed to delete assignment';
          toast.error(msg);
        }
      },
    });
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  };

  const handleToggleSelectAllContacts = () => {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (areAllVisibleContactsSelected) {
        paginatedContacts.forEach((contact) => next.delete(contact.id));
        return next;
      }
      paginatedContacts.forEach((contact) => next.add(contact.id));
      return next;
    });
  };

  const handleBulkDeleteContacts = () => {
    const idsToDelete = Array.from(selectedContacts);
    if (idsToDelete.length === 0) return;

    openDeleteConfirm({
      title: `Delete ${idsToDelete.length} message(s)?`,
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        setBulkDeletingContacts(true);
        try {
          await Promise.all(idsToDelete.map((contactId) => axios.delete(`${API_URL}/api/contact/${contactId}`)));
          toast.success(`${idsToDelete.length} message(s) deleted`);
          if (selectedContact && idsToDelete.includes(selectedContact.id)) {
            setSelectedContact(null);
          }
          setSelectedContacts(new Set());
          fetchData();
        } catch (err) {
          toast.error('Failed to delete selected messages');
        } finally {
          setBulkDeletingContacts(false);
        }
      },
    });
  };

  const handleBulkMarkContactsRead = async () => {
    const idsToMark = Array.from(selectedContacts);
    if (idsToMark.length === 0) return;

    setBulkMarkingContactsRead(true);
    try {
      const results = await Promise.allSettled(
        idsToMark.map((contactId) => axios.put(`${API_URL}/api/contact/${contactId}/read`))
      );
      const successCount = results.filter((result) => result.status === 'fulfilled').length;

      if (successCount > 0) {
        toast.success(`${successCount} message(s) marked as read`);
        setSelectedContacts(new Set());
        fetchData();
      }

      if (successCount !== idsToMark.length) {
        toast.error('Some selected messages could not be marked as read');
      }
    } catch (err) {
      toast.error('Failed to mark selected messages as read');
    } finally {
      setBulkMarkingContactsRead(false);
    }
  };

  useEffect(() => {
    setSelectedContacts((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(filteredContacts.map((contact) => contact.id));
      const next = new Set(Array.from(prev).filter((contactId) => validIds.has(contactId)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredContacts]);

  const handleDeleteIssue = async (issueId) => {
    openDeleteConfirm({
      title: 'Delete Issue?',
      description: 'This action cannot be undone.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/issues/${issueId}`);
          toast.success('Issue deleted');
          setSelectedIssue(null);
          handleSearchIssues(issueSearch);
        } catch (err) {
          const msg = err.response?.data?.detail || 'Failed to delete issue';
          toast.error(msg);
        }
      },
    });
  };

  // Edit user
  const openEditUser = (u) => {
    setEditUser(u);
    setAdminUserErrors({});
    setEditUserForm({ 
      name: u.name, 
      email: u.email, 
      phone: u.phone || '', 
      ownership_type: u.ownership_type ?? null,
      company_name: u.company_name || '',
      business_address: u.business_address || '',
      vat_number: u.vat_number || '',
      company_registration: u.company_registration || '',
      website: u.website || '',
      role: u.role, 
      is_active: u.is_active !== false, 
      is_approved: u.is_approved !== false 
    });
  };

  const handleSaveUser = async () => {
    const fullName = validatePersonFullName(editUserForm.name, 'Full Name');
    if (!fullName.valid) {
      toast.error(fullName.error);
      return;
    }
    if (editUserForm.role === 'landlord' && editUserForm.ownership_type === 'company') {
      const nextErrors = {};
      if (!editUserForm.company_name?.trim()) nextErrors.edit_company_name = 'Company name is required for company ownership.';
      if (!editUserForm.company_registration?.trim()) nextErrors.edit_company_registration = 'Company registration is required for company ownership.';
      if (Object.keys(nextErrors).length > 0) {
        setAdminUserErrors(nextErrors);
        toast.error('Complete the company ownership fields before saving');
        return;
      }
    }

    setAdminUserErrors({});
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${editUser.id}`, { ...editUserForm, name: fullName.value });
      toast.success('User updated');
      setEditUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${resetPwUser.id}/reset-password`, { new_password: newPassword });
      toast.success('Password reset');
      setResetPwUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  // Edit property
  const openEditProperty = (p) => {
    setEditProperty(p);
    setEditPropertyForm({
      title: p.title,
      address: p.address,
      postcode: p.postcode,
      rent: p.rent,
      floors: p.floors != null ? String(p.floors) : '',
      status: p.status === 'maintenance' ? 'maintenance' : 'available',
      property_type: p.property_type,
      landlord_id: p.landlord_id,
      is_occupied: Boolean(p.is_occupied),
      bedrooms: p.bedrooms != null ? String(p.bedrooms) : '',
      bathrooms: p.bathrooms != null ? String(p.bathrooms) : '',
      description: p.description || '',
      has_gas_supply: Boolean(p.has_gas_supply),
      furnished_appliances: Boolean(p.furnished_appliances),
      furnishing: p.furnishing || 'none',
      water_system_type: p.water_system_type || 'none',
      licensing_status: p.licensing_status || 'no',
    });
  };

  const handleSaveProperty = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editPropertyForm,
        floors: editPropertyForm.floors === '' ? 0 : Math.max(0, parseInt(editPropertyForm.floors, 10) || 0),
      };
      await axios.put(`${API_URL}/api/properties/${editProperty.id}`, payload);
      toast.success('Property updated');
      setEditProperty(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update property');
    } finally {
      setSaving(false);
    }
  };

  // Edit rent
  const openEditRent = (r) => {
    setEditRent(r);
    setEditRentForm({ rent_amount: r.rent_amount, due_day_of_month: r.due_day_of_month, payment_method: r.payment_method || 'bank_transfer', status: r.status, notes: r.notes || '' });
  };

  const handleSaveRent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/rent/${editRent.id}`, editRentForm);
      toast.success('Rent schedule updated');
      setEditRent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rent');
    } finally {
      setSaving(false);
    }
  };

  // End assignment
  const handleEndAssignment = async (assignmentId) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    const prop = properties.find(p => p.id === assignment?.property_id);
    const remainingActive = assignments.filter(a => a.property_id === prop?.id && a.id !== assignmentId && a.status === 'active');
    try {
      await axios.put(`${API_URL}/api/tenant-assignments/${assignmentId}`, {
        status: 'ended',
        end_date: new Date().toISOString().split('T')[0]
      });
      toast.success('Assignment ended');
      fetchData();
      if (prop?.is_occupied && remainingActive.length === 0) {
        toast.info(`"${prop.title}" may now be unoccupied. Update occupancy?`, {
          action: { label: 'Mark Unoccupied', onClick: () => suggestOccupancy(prop.id, false) },
          duration: 8000,
        });
      }
    } catch (error) {
      toast.error('Failed to end assignment');
    }
  };

  // Edit assignment
  const openEditAssignment = (a) => {
    setEditAssignment(a);
    setEditAssignmentForm({ property_id: a.property_id, start_date: a.start_date, end_date: a.end_date || '', status: a.status, notes: a.notes || '' });
  };

  const handleSaveAssignment = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/tenant-assignments/${editAssignment.id}`, editAssignmentForm);
      toast.success('Assignment updated');
      setEditAssignment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizeAssignmentTenancy = async (assignmentId) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/tenant-assignments/${assignmentId}/finalize-tenancy`);
      toast.success('Tenancy finalized');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize tenancy');
    } finally {
      setSaving(false);
    }
  };

  // === Create handlers ===
  const handleCreateUser = async () => {
    const fullName = validatePersonFullName(createUserForm.name, 'Full Name');
    if (!fullName.valid) {
      toast.error(fullName.error);
      return;
    }
    if (createUserForm.role === 'landlord' && createUserForm.ownership_type === 'company') {
      const nextErrors = {};
      if (!createUserForm.company_name?.trim()) nextErrors.create_company_name = 'Company name is required for company ownership.';
      if (!createUserForm.company_registration?.trim()) nextErrors.create_company_registration = 'Company registration is required for company ownership.';
      if (Object.keys(nextErrors).length > 0) {
        setAdminUserErrors(nextErrors);
        toast.error('Complete the company ownership fields before creating the landlord');
        return;
      }
    }

    setAdminUserErrors({});
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/create`, { ...createUserForm, name: fullName.value });
      toast.success('User created');
      setCreateUser(false);
      setCreateUserForm({ name: '', email: '', password: '', role: 'tenant', phone: '', ownership_type: 'individual', company_name: '', company_registration: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProperty = async () => {
    if (!createPropertyForm.landlord_id) { toast.error('Please select a landlord'); return; }
    setSaving(true);
    try {
      const payload = {
        ...createPropertyForm,
        floors: createPropertyForm.floors === '' ? 0 : Math.max(0, parseInt(createPropertyForm.floors, 10) || 0),
      };
      await axios.post(`${API_URL}/api/properties`, payload);
      toast.success('Property created');
      setCreateProperty(false);
      setCreatePropertyForm({ title: '', address: '', postcode: '', rent: 0, floors: '', property_type: 'flat', landlord_id: '', is_occupied: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create property');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateIssue = async () => {
    if (!createIssueForm.property_id) { toast.error('Please select a property'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/issues`, {
        ...createIssueForm,
        sub_category: createIssueForm.sub_category || null,
        custom_title: createIssueForm.category === 'Other' ? (createIssueForm.custom_title.trim() || null) : null,
      });
      toast.success('Issue created');
      setCreateIssue(false);
      setCreateIssueForm({ property_id: '', description: '', category: DEFAULT_ISSUE_CATEGORY, sub_category: '', custom_title: '', priority: 'medium' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create issue');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRent = async () => {
    if (!createRentForm.property_id || !createRentForm.tenant_id) { toast.error('Please select a property and one of its active portal tenants'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/rent`, createRentForm);
      toast.success('Rent schedule created');
      setCreateRent(false);
      setCreateRentForm({ property_id: '', tenant_id: '', rent_amount: 0, due_day_of_month: 1, payment_method: 'bank_transfer', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rent schedule');
    } finally {
      setSaving(false);
    }
  };

  const createNextRentScheduleFromExisting = async (rent) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/rent`, {
        property_id: rent.property_id,
        tenant_id: rent.tenant_id || null,
        rent_amount: parseFloat(rent.rent_amount),
        due_day_of_month: parseInt(rent.due_day_of_month, 10),
        payment_method: rent.payment_method || 'bank_transfer',
        notes: rent.notes || null,
      });
      toast.success('Next rent schedule created');
      fetchData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rent schedule');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openCreateRentForm = () => {
    setCreateRent(true);
    setCreateRentForm({ property_id: '', tenant_id: '', rent_amount: 0, due_day_of_month: 1, payment_method: 'bank_transfer', notes: '' });
  };

  const handleAddRentSchedule = async () => {
    const latestSchedules = getLatestRentSchedulePerChain(rentSchedules);
    if (latestSchedules.length === 1) {
      await createNextRentScheduleFromExisting(latestSchedules[0]);
      return;
    }

    openCreateRentForm();
  };

  // Occupancy suggestion helper: patches is_occupied without auto-enforcing
  const suggestOccupancy = async (propertyId, isOccupied) => {
    try {
      await axios.patch(`${API_URL}/api/properties/${propertyId}`, { is_occupied: isOccupied });
      fetchData();
      toast.success(`Property marked as ${isOccupied ? 'occupied' : 'unoccupied'}`);
    } catch {
      toast.error('Failed to update occupancy');
    }
  };

  const handleCreateAssignment = async () => {
    const normalizedTenantEmail = normalizeEmailValue(createAssignmentForm.tenant_email || '');
    if (!createAssignmentForm.property_id || !normalizedTenantEmail) { toast.error('Please select a property and enter a tenant email'); return; }
    setSaving(true);
    try {
      const existingUser = findExistingUserByEmail(normalizedTenantEmail);
      if (existingUser && existingUser.role !== 'tenant') {
        toast.error('That email already belongs to a non-tenant account');
        return;
      }

      setCreateAssignment(false);
      const prop = properties.find(p => p.id === createAssignmentForm.property_id);

      if (existingUser) {
        await axios.post(`${API_URL}/api/tenant-assignments`, {
          tenant_email: normalizedTenantEmail,
          property_id: createAssignmentForm.property_id,
          start_date: createAssignmentForm.start_date || undefined,
          notes: createAssignmentForm.notes || undefined,
        });
        toast.success('Assignment created for existing tenant account');
      } else {
        const inviteResponse = await axios.post(`${API_URL}/api/landlord/invites`, {
          email: normalizedTenantEmail,
          property_id: createAssignmentForm.property_id,
          start_date: createAssignmentForm.start_date || undefined,
          notes: createAssignmentForm.notes || undefined,
        });
        if (createAssignmentForm.send_invite_email !== false) {
          await axios.post(`${API_URL}/api/landlord/invites/${inviteResponse.data.id}/send-email`);
          toast.success('Invite created and emailed');
        } else {
          toast.success('Invite created');
        }
      }

      setCreateAssignmentForm({ tenant_email: '', property_id: '', start_date: '', notes: '', send_invite_email: true });
      fetchData();
      if (existingUser && prop && !prop.is_occupied) {
        toast.info(`"${prop.title}" is marked as unoccupied. Update occupancy?`, {
          action: { label: 'Mark Occupied', onClick: () => suggestOccupancy(prop.id, true) },
          duration: 8000,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    } finally {
      setSaving(false);
    }
  };

  // === CSV Export helper ===
  const handleExportCsv = async (url, filename, setExporting) => {
    setExporting(true);
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error(`Failed to export ${filename}`);
    } finally {
      setExporting(false);
    }
  };

  // === Send Rent Reminders ===
  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await axios.post(`${API_URL}/api/rent/send-reminders`);
      const { total, reminders_sent } = res.data;
      if (total > 0) {
        toast.success(`Sent ${total} reminders (${reminders_sent.upcoming} upcoming, ${reminders_sent.due_today} due today, ${reminders_sent.overdue} overdue)`);
      } else {
        toast.info('No reminders to send at this time');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  // === Backup controls ===
  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      await axios.post(`${API_URL}/api/admin/backup/create`);
      toast.success('Backup created successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleViewBackups = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/backups`);
      setBackupsData(res.data || []);
      setViewingBackups(true);
    } catch {
      toast.error('Failed to fetch backups');
    }
  };

  // === Badge helpers ===
  const getStatusBadge = (status) => {
    const m = { reported: 'bg-red-100 text-red-700', in_progress: 'bg-yellow-100 text-yellow-700', resolved: 'bg-green-100 text-green-700' };
    return <Badge className={m[status] || ''}>{status?.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const m = { urgent: 'bg-red-600 text-white', high: 'bg-orange-100 text-orange-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-gray-100 text-gray-600' };
    return <Badge className={m[priority] || ''} variant="outline">{priority}</Badge>;
  };

  const getRoleBadge = (role) => {
    const m = { admin: ['bg-purple-100 text-purple-700', <ShieldCheck key="i" className="h-3 w-3 mr-1" />], landlord: ['bg-blue-100 text-blue-700', <Building2 key="i" className="h-3 w-3 mr-1" />], tenant: ['bg-green-100 text-green-700', <UserCheck key="i" className="h-3 w-3 mr-1" />] };
    const [cls, icon] = m[role] || ['', null];
    return <Badge className={cls}>{icon}{role}</Badge>;
  };

  const getUserStatusBadge = (u) => {
    if (u.is_active === false) return <Badge className="bg-red-100 text-red-700">Deactivated</Badge>;
    if (!u.is_approved) return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  const tableActionGroupClass = 'flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end';
  const tableActionButtonClass = 'w-full sm:w-auto sm:min-w-[96px]';

  // === User actions row (reused across all user-like tabs) ===
  const UserActions = ({ u }) => (
    <div className={`${tableActionGroupClass} items-stretch sm:items-center`}>
      {!u.is_approved && u.role === 'landlord' && (
        <Button
          className={tableActionButtonClass}
          size="sm"
          onClick={() => handleApproveUser(u.id)}
          disabled={!u.is_email_verified}
          title={!u.is_email_verified ? 'Cannot approve — email not yet verified' : undefined}
          data-testid={`approve-user-${u.id}`}
        >Approve</Button>
      )}
      {u.role === 'tenant' && (
        <Button
          className={tableActionButtonClass}
          size="sm"
          variant={u.move_intent_enabled ? 'default' : 'outline'}
          onClick={() => handleToggleMoveIntent(u.id, u.move_intent_enabled)}
          title={u.move_intent_enabled ? 'Disable move intent for this tenant' : 'Enable move intent for this tenant'}
          data-testid={`move-intent-toggle-${u.id}`}
        >
          {u.move_intent_enabled ? 'Move Intent: On' : 'Move Intent: Off'}
        </Button>
      )}
      <Button className={tableActionButtonClass} size="sm" variant="outline" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`}>Edit</Button>
      <Button className={tableActionButtonClass} size="sm" variant="outline" onClick={() => { setResetPwUser(u); setNewPassword(''); }} data-testid={`reset-pw-${u.id}`}>Reset PW</Button>
      {user?.role === 'admin' && (
        <Button
          className={tableActionButtonClass}
          size="sm"
          variant="destructive"
          onClick={() => handleDeactivateUser(u.id)}
          disabled={u.is_active === false}
          data-testid={`deactivate-user-${u.id}`}
        >
          {u.is_active === false ? 'Deactivated' : 'Deactivate'}
        </Button>
      )}
    </div>
  );

  const handleDeactivateUser = async (userId) => {
    openDeleteConfirm({
      title: 'Deactivate user?',
      description: 'This removes portal access without deleting account records or operational history.',
      onConfirm: async () => {
        try {
          await axios.put(`${API_URL}/api/admin/users/${userId}`, { is_active: false });
          toast.success('User deactivated');
          fetchData();
        } catch (err) {
          const msg = err.response?.data?.detail || 'Failed to deactivate user';
          toast.error(msg);
        }
      },
    });
  };

  const topAbuseSenders = deliveryStats?.abuse?.top_abuse_senders || [];

  const openMonitoringSuspendConfirm = (matchedUser, reasons = []) => {
    if (!matchedUser?.id) return;
    const isCurrentlySuspended = matchedUser.is_active === false;
    const actionLabel = isCurrentlySuspended ? 'Unsuspend' : 'Suspend';
    const riskContext = Array.isArray(reasons) && reasons.length > 0
      ? ` Signals: ${reasons.join(', ')}.`
      : '';

    openDeleteConfirm({
      title: `${actionLabel} user?`,
      description: isCurrentlySuspended
        ? 'This restores portal access for the matched user account.'
        : `This removes portal access without deleting account records or operational history.${riskContext}`,
      actionLabel,
      isDestructive: !isCurrentlySuspended,
      onConfirm: async () => {
        try {
          await axios.put(`${API_URL}/api/admin/users/${matchedUser.id}`, { is_active: isCurrentlySuspended });
          toast.success(isCurrentlySuspended ? 'User unsuspended' : 'User suspended');
          fetchData();
        } catch (err) {
          const msg = err.response?.data?.detail || (isCurrentlySuspended ? 'Failed to unsuspend user' : 'Failed to suspend user');
          toast.error(msg);
        }
      },
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-navy-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-navy-50 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-3xl font-bold text-navy-900" data-testid="admin-dashboard-title">Admin Dashboard</h1>
              <button
                onClick={() => {
                  if (activeTab === 'email-logs') {
                    fetchEmailLogs();
                    fetchEmailProviderSettings();
                  } else if (activeTab === 'api-usage') {
                    fetchApiUsage();
                  } else {
                    fetchData(0, true);
                  }
                }}
                disabled={
                  activeTab === 'email-logs' ? emailLogsLoading :
                  activeTab === 'api-usage' ? apiUsageLoading :
                  refreshing
                }
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                title="Refresh dashboard data"
              >
                {(activeTab === 'email-logs' ? emailLogsLoading : activeTab === 'api-usage' ? apiUsageLoading : refreshing)
                  ? <Loader2 className="h-5 w-5 animate-spin" />
                  : <RefreshCw className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            {[
              { label: 'Properties', val: stats?.properties?.total || 0, icon: Building2, color: 'royal', tab: 'properties' },
              { label: 'Issues', val: stats?.issues?.reported || 0, icon: ClipboardList, color: 'yellow', tab: 'issues', suffix: ' open' },
              { label: 'Users', val: stats?.users?.total || 0, icon: Users, color: 'blue', tab: 'users' },
              { label: 'Rent Due', val: (stats?.rent?.due || 0) + (stats?.rent?.overdue || 0), icon: CreditCard, color: 'orange', tab: 'rent' },
              { label: 'Messages', val: stats?.contacts?.unread || 0, icon: MessageSquare, color: 'green', tab: 'contacts' },
              { label: 'Pending', val: stats?.users?.pending_landlords || 0, icon: Clock, color: 'red', tab: 'landlords' },
            ].map(s => (
              <Card key={s.label} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleTabChange(s.tab)} data-testid={`stat-card-${s.label.toLowerCase().replace(' ', '-')}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg bg-${s.color}-100`}>
                      <s.icon className={`h-4 w-4 text-${s.color}-600`} />
                    </div>
                    <div>
                      <div className="text-xl font-bold leading-tight">{s.val}{s.suffix || ''}</div>
                      <div className="text-xs text-muted-foreground">{s.label}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Last refreshed */}
          {lastRefreshedAt && (
            <p className="text-xs text-muted-foreground mb-4">
              Last refreshed: {Math.round((Date.now() - lastRefreshedAt) / 60000) < 1
                ? 'just now'
                : `${Math.round((Date.now() - lastRefreshedAt) / 60000)} min ago`}
            </p>
          )}

          {/* Backup controls */}
          <div className="flex items-center gap-3 mb-6 p-3 bg-muted/40 rounded-lg border border-border/50">
            <span className="text-sm font-medium text-muted-foreground">Database:</span>
            <Button
              size="sm"
              variant="outline"
              className="min-w-0"
              onClick={handleCreateBackup}
              disabled={creatingBackup}
            >
              {creatingBackup ? <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" /> : null}
              <span className="truncate">
                {creatingBackup ? (
                  <>
                    <span className="sm:hidden">Creating…</span>
                    <span className="hidden sm:inline">Creating backup…</span>
                  </>
                ) : (
                  'Create Backup'
                )}
              </span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleViewBackups}
            >
              View Backups
            </Button>
          </div>

          {/* Tabs */}
          <div ref={tabScrollRef}>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-8">
            <div className="w-full overflow-x-auto pb-1">
              <TabsList className="inline-flex min-w-max justify-start sm:w-full sm:min-w-0 sm:flex-wrap sm:overflow-visible">
              <TabsTrigger value="users" data-testid="users-tab"><Users className="h-4 w-4 mr-1" />All Users{tabBadge(countNewSince(users, 'users'))}</TabsTrigger>
              <TabsTrigger value="landlords" data-testid="landlords-tab"><Building2 className="h-4 w-4 mr-1" />Landlords{tabBadge(countNewSince(landlords, 'landlords'), landlords.filter(l => !l.is_approved).length)}</TabsTrigger>
              <TabsTrigger value="engagement" data-testid="engagement-tab"><Activity className="h-4 w-4 mr-1" />Engagement</TabsTrigger>
              <TabsTrigger value="properties" data-testid="properties-tab"><Building2 className="h-4 w-4 mr-1" />Properties{tabBadge(countNewSince(properties, 'properties'))}</TabsTrigger>
              <TabsTrigger value="assignments" data-testid="assignments-tab"><Link2 className="h-4 w-4 mr-1" />Assignments{tabBadge(countNewSince(assignments, 'assignments'))}</TabsTrigger>
              <TabsTrigger value="applications" data-testid="applications-tab"><FileCheck className="h-4 w-4 mr-1" />Applications{tabBadge(countNewSince(applications, 'applications'), applications.filter(a => getApplicationDecisionStatus(a) === 'pending').length)}</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="contacts-tab"><MessageSquare className="h-4 w-4 mr-1" />Contacts{tabBadge(countNewSince(contacts, 'contacts'), contacts.filter(c => !c.is_read).length)}</TabsTrigger>
              <TabsTrigger value="issues" data-testid="issues-tab"><ClipboardList className="h-4 w-4 mr-1" />Issues{tabBadge(countNewSince(issues, 'issues'), issues.filter(i => i.status !== 'resolved').length)}</TabsTrigger>
              <TabsTrigger value="rent" data-testid="rent-tab"><CreditCard className="h-4 w-4 mr-1" />Rent{tabBadge(countNewSince(rentSchedules, 'rent'))}</TabsTrigger>
              <TabsTrigger value="document-delivery" data-testid="document-delivery-tab"><FileText className="h-4 w-4 mr-1" />Document Delivery</TabsTrigger>
              <TabsTrigger value="audit" data-testid="audit-tab"><FileText className="h-4 w-4 mr-1" />Document Audit</TabsTrigger>
              <TabsTrigger value="compliance" data-testid="compliance-tab"><ShieldCheck className="h-4 w-4 mr-1" />Compliance</TabsTrigger>
              <TabsTrigger value="activity" data-testid="activity-tab"><Activity className="h-4 w-4 mr-1" />Activity Log</TabsTrigger>
              <TabsTrigger value="health-check-audit" data-testid="health-check-audit-tab"><HeartPulse className="h-4 w-4 mr-1" />Health Check Audit</TabsTrigger>
              <TabsTrigger value="email-logs" data-testid="email-logs-tab"><Mail className="h-4 w-4 mr-1" />Email Logs <span className="ml-1 text-xs text-muted-foreground">({emailLogsTotal})</span></TabsTrigger>
              <TabsTrigger value="signing" data-testid="signing-tab"><FileText className="h-4 w-4 mr-1" />Signing <span className="ml-1 text-xs text-muted-foreground">({signingVersions.length})</span></TabsTrigger>
              <TabsTrigger value="market-rate-history" data-testid="market-rate-history-tab"><TrendingUp className="h-4 w-4 mr-1" />Market Rate</TabsTrigger>
              <TabsTrigger value="monitoring" data-testid="monitoring-tab"><ShieldAlert className="h-4 w-4 mr-1" />Monitoring</TabsTrigger>
              <TabsTrigger value="api-usage" data-testid="api-usage-tab"><Zap className="h-4 w-4 mr-1" />API Usage</TabsTrigger>
              </TabsList>
            </div>

            {/* ===== ISSUES TAB ===== */}
            <TabsContent value="issues">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="Search issues..." value={issueSearch} onChange={(e) => setIssueSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchIssues(issueSearch)} className="pl-9" data-testid="admin-issue-search" />
                    </div>
                    <Select value={issueStatusFilter} onValueChange={setIssueStatusFilter}>
                      <SelectTrigger className="w-full sm:w-40" data-testid="issue-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="reported">Reported</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={issuePriorityFilter} onValueChange={setIssuePriorityFilter}>
                      <SelectTrigger className="w-full sm:w-40" data-testid="issue-priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priority</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap xl:ml-auto xl:w-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => handleExportCsv(`${API_URL}/api/export/issues?format=csv`, 'issues', setExportingIssues)}
                        disabled={exportingIssues}
                      >
                        {exportingIssues ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                        Export CSV
                      </Button>
                      <Button size="sm" className="w-full sm:w-auto" onClick={() => setCreateIssue(true)} data-testid="create-issue-btn">+ New Issue</Button>
                      {selectedIssues.size > 0 && (
                        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                          <span className="text-sm text-muted-foreground">{selectedIssues.size} selected</span>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="w-full sm:w-auto"
                            onClick={handleBulkDeleteIssues}
                            disabled={bulkDeletingIssues}
                          >
                            {bulkDeletingIssues ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                            Delete Selected
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={areAllVisibleIssuesSelected}
                          onCheckedChange={handleToggleSelectAllIssues}
                          aria-label="Select all issues"
                        />
                      </TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead>Tenancy Participants</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedIssues.map(issue => (
                      <TableRow key={issue.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIssues.has(issue.id)}
                            onCheckedChange={() => toggleIssueSelection(issue.id)}
                            aria-label={`Select issue ${issue.title}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">{issue.title}</TableCell>
                        <TableCell className="text-sm">{issue.property_title || '-'}</TableCell>
                        <TableCell className="text-sm">{issue.tenant_name || '-'}</TableCell>
                        <TableCell>{getStatusBadge(issue.status)}</TableCell>
                        <TableCell>{getPriorityBadge(issue.priority)}</TableCell>
                        <TableCell className="text-sm">{new Date(issue.created_at).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell>
                          <div className={tableActionGroupClass}>
                            <Button className={tableActionButtonClass} size="sm" variant="outline" onClick={() => { setSelectedIssue(issue); setIssueNotes(issue.admin_notes || ''); }} data-testid={`view-issue-${issue.id}`}>
                              Edit
                            </Button>
                            <Button className={tableActionButtonClass} size="sm" variant="destructive" onClick={() => handleDeleteIssue(issue.id)} data-testid={`delete-issue-${issue.id}`}>Delete</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={issuesPagination} itemLabel="issues" />
              </Card>
            </TabsContent>

            {/* ===== PROPERTIES TAB ===== */}
            <TabsContent value="properties">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="Search by address..." value={propertySearch} onChange={(e) => setPropertySearch(e.target.value)} className="pl-9" data-testid="admin-property-search" />
                    </div>
                    <Select value={propertyLandlordFilter} onValueChange={setPropertyLandlordFilter}>
                      <SelectTrigger className="w-full sm:w-48" data-testid="property-landlord-filter"><SelectValue placeholder="All Landlords" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Landlords</SelectItem>
                        {landlords.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={propertyStatusFilter} onValueChange={setPropertyStatusFilter}>
                      <SelectTrigger className="w-full sm:w-40" data-testid="property-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="rented">Rented</SelectItem>
                      </SelectContent>
                    </Select>
                    {/* TODO: Add /api/export/properties endpoint */}
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap xl:ml-auto xl:w-auto">
                      <Button size="sm" variant="outline" className="w-full sm:w-auto" disabled title="Coming soon">
                        <Download className="h-4 w-4 mr-1" />Export CSV
                      </Button>
                      <Button
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() => {
                          setCreateProperty(true);
                        }}
                        data-testid="create-property-btn"
                      >
                        + New Property
                      </Button>
                      {selectedProperties.size > 0 && (
                        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                          <span className="text-sm text-muted-foreground">{selectedProperties.size} selected</span>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="w-full sm:w-auto"
                            onClick={handleBulkDeleteProperties}
                            disabled={bulkDeletingProperties}
                          >
                            {bulkDeletingProperties ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                            Delete Selected
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={areAllVisiblePropertiesSelected}
                          onCheckedChange={handleToggleSelectAllProperties}
                          aria-label="Select all properties"
                        />
                      </TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Landlord</TableHead>
                      <TableHead>Tenants</TableHead>
                      <TableHead>Issues</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProperties.map(prop => (
                      <TableRow key={prop.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedProperties.has(prop.id)}
                            onCheckedChange={() => togglePropertySelection(prop.id)}
                            aria-label={`Select property ${prop.title}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{prop.title}</TableCell>
                        <TableCell className="text-sm">{prop.address}</TableCell>
                        <TableCell className="text-sm">{prop.landlord_name}</TableCell>
                        <TableCell>{prop.tenant_count}</TableCell>
                        <TableCell>
                          {prop.open_issues > 0 ? <Badge className="bg-yellow-100 text-yellow-700">{prop.open_issues}</Badge> : <span className="text-sm text-muted-foreground">0</span>}
                        </TableCell>
                        <TableCell>
                          {prop.lifecycle_status === 'archived'
                            ? <Badge className="bg-gray-100 text-gray-500">Archived</Badge>
                            : prop.is_occupied
                              ? <Badge className="bg-amber-100 text-amber-700">Occupied</Badge>
                              : prop.status === 'maintenance'
                                ? <Badge className="bg-yellow-100 text-yellow-700">Maintenance</Badge>
                                : <Badge className="bg-green-100 text-green-700">Available</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className={tableActionGroupClass}>
                            <Button className={tableActionButtonClass} size="sm" variant="outline" onClick={() => openEditProperty(prop)} data-testid={`edit-property-${prop.id}`}>Edit</Button>
                            <Button className={tableActionButtonClass} size="sm" variant="destructive" onClick={() => handleDeleteProperty(prop.id)} data-testid={`delete-property-${prop.id}`}>Delete</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={propertiesPagination} itemLabel="properties" />
              </Card>
            </TabsContent>

            {/* ===== RENT TAB ===== */}
            <TabsContent value="rent">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
                    <CardTitle className="text-lg">Rent Schedules</CardTitle>
                    <div className="relative min-w-[240px] flex-1 max-w-md">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search rent records..."
                        value={rentSearch}
                        onChange={(e) => setRentSearch(e.target.value)}
                        className="pl-9"
                        data-testid="admin-rent-search"
                      />
                    </div>
                    <Select value={rentStatusFilter} onValueChange={setRentStatusFilter}>
                      <SelectTrigger className="w-full sm:w-40" data-testid="rent-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="due">Due</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap xl:ml-auto xl:w-auto xl:justify-end">
                      {selectedRentSchedules.size > 0 && (
                        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                          <span className="text-sm text-muted-foreground">{selectedRentSchedules.size} selected</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="min-w-0 w-full sm:w-auto"
                            onClick={handleBulkUnmarkRent}
                            disabled={bulkUnmarkingRent}
                          >
                            {bulkUnmarkingRent ? <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4 shrink-0" />}
                            <span className="truncate">Unmark Paid</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="min-w-0 w-full sm:w-auto"
                            onClick={handleBulkDeleteRent}
                            disabled={bulkDeletingRent}
                          >
                            {bulkDeletingRent ? <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4 shrink-0" />}
                            <span className="truncate">Delete Selected</span>
                          </Button>
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="min-w-0 w-full text-muted-foreground sm:w-auto"
                        onClick={() => setReminderRulesOpen(true)}
                      >
                        <Info className="mr-1 h-4 w-4 shrink-0" />
                        <span className="truncate">
                          <span className="sm:hidden">Rules</span>
                          <span className="hidden sm:inline">Reminder rules</span>
                        </span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-w-0 w-full sm:w-auto"
                        onClick={() => handleExportCsv(`${API_URL}/api/export/rent?format=csv`, 'rent', setExportingRent)}
                        disabled={exportingRent}
                      >
                        {exportingRent ? <Loader2 className="mr-1 h-4 w-4 shrink-0 animate-spin" /> : <Download className="mr-1 h-4 w-4 shrink-0" />}
                        <span className="truncate">Export CSV</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-w-0 w-full sm:w-auto"
                        onClick={handleSendReminders}
                        disabled={sendingReminders}
                      >
                        {sendingReminders ? <Loader2 className="mr-1 h-4 w-4 shrink-0 animate-spin" /> : <Bell className="mr-1 h-4 w-4 shrink-0" />}
                        <span className="truncate">
                          {sendingReminders ? (
                            <>
                              <span className="sm:hidden">Sending…</span>
                              <span className="hidden sm:inline">Sending reminders…</span>
                            </>
                          ) : (
                            <>
                              <span className="sm:hidden">Reminders</span>
                              <span className="hidden sm:inline">Send Reminders</span>
                            </>
                          )}
                        </span>
                      </Button>
                      <Button size="sm" className="min-w-0 w-full sm:w-auto" onClick={handleAddRentSchedule} disabled={saving} data-testid="create-rent-btn"><span className="truncate">+ New Schedule</span></Button>
                    </div>
                  </div>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={areAllVisibleRentSelected}
                          onCheckedChange={handleToggleSelectAllRent}
                          aria-label="Select all rent schedules"
                        />
                      </TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead>Tenancy Participants</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRent.map(r => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedRentSchedules.has(r.id)}
                            onCheckedChange={() => toggleRentSelection(r.id)}
                            aria-label={`Select rent schedule ${r.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-sm">{r.property_title}</TableCell>
                        <TableCell className="text-sm">{r.tenant_name}</TableCell>
                        <TableCell className="font-medium">{'\u00A3'}{r.rent_amount?.toLocaleString()}</TableCell>
                        <TableCell className="text-sm">{r.next_due_date}</TableCell>
                        <TableCell>
                          {r.status === 'overdue' ? <Badge className="bg-red-100 text-red-700">Overdue</Badge>
                            : r.status === 'paid' ? <Badge className="bg-green-100 text-green-700">Paid</Badge>
                            : <Badge className="bg-yellow-100 text-yellow-700">Due</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className={tableActionGroupClass}>
                            <Button
                              className={tableActionButtonClass}
                              size="sm"
                              variant="outline"
                              onClick={() => openEditRent(r)}
                              disabled={r.status === 'paid'}
                              title={r.status === 'paid' ? 'Paid historical schedules cannot be edited' : 'Edit rent schedule'}
                              data-testid={`edit-rent-${r.id}`}
                            >
                              Edit
                            </Button>
                            {r.status === 'paid' && (
                              <Button
                                className={tableActionButtonClass}
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnmarkRent(r.id)}
                                data-testid={`unmark-rent-${r.id}`}
                              >
                                Unmark
                              </Button>
                            )}
                            <Button
                              className={tableActionButtonClass}
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteRent(r.id)}
                              disabled={r.status === 'paid'}
                              title={r.status === 'paid' ? 'Paid rent schedule cannot be deleted' : 'Delete rent schedule'}
                              data-testid={`delete-rent-${r.id}`}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredRent.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No rent schedules found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={rentPagination} itemLabel="rent schedules" />
              </Card>
            </TabsContent>

            {/* ===== ALL USERS TAB ===== */}
            <TabsContent value="users">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="Search by name or email..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="pl-9" data-testid="admin-user-search" />
                    </div>
                    <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                      <SelectTrigger className="w-full sm:w-36" data-testid="user-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="landlord">Landlord</SelectItem>
                        <SelectItem value="tenant">Tenant</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
                      <SelectTrigger className="w-full sm:w-36" data-testid="user-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Deactivated</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap xl:ml-auto xl:w-auto">
                      {selectedUsers.size > 0 && (
                        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                          <span className="text-sm text-muted-foreground">{selectedUsers.size} selected</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full sm:w-auto"
                            onClick={() => handleBulkApproveUsers(false)}
                            disabled={bulkApprovingUsers || bulkDeletingUsers}
                          >
                            {bulkApprovingUsers ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Approve Selected
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="w-full sm:w-auto"
                            onClick={() => handleBulkDeactivateUsers(false)}
                            disabled={bulkApprovingUsers || bulkDeletingUsers}
                          >
                            {bulkDeletingUsers ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                            Deactivate Selected
                          </Button>
                        </div>
                      )}
                      {/* TODO: Add /api/export/users endpoint */}
                      <Button size="sm" variant="outline" className="w-full sm:w-auto" disabled title="Coming soon">
                        <Download className="h-4 w-4 mr-1" />Export CSV
                      </Button>
                      <Button size="sm" className="w-full sm:w-auto" onClick={() => setCreateUser(true)} data-testid="create-user-btn">+ New User</Button>
                    </div>
                  </div>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={areAllVisibleUsersSelected}
                          onCheckedChange={handleToggleSelectAllUsers}
                          aria-label="Select all users"
                        />
                      </TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>ToS Accepted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedUsers.map(u => (
                      <TableRow key={u.id} className={u.is_active === false ? 'opacity-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedUsers.has(u.id)}
                            onCheckedChange={() => toggleUserSelection(u.id)}
                            aria-label={`Select user ${u.name}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-sm">{u.email}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{u.phone || '-'}</TableCell>
                        <TableCell>{getRoleBadge(u.role)}</TableCell>
                        <TableCell>{getUserStatusBadge(u)}</TableCell>
                        <TableCell className="text-sm">{new Date(u.created_at).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell className="text-sm">
                          {u.tos_accepted_at
                            ? <span className="text-green-700" title={new Date(u.tos_accepted_at).toLocaleString('en-GB')}>
                                ✓ {new Date(u.tos_accepted_at).toLocaleDateString('en-GB')}
                              </span>
                            : <span className="text-yellow-600">Pending</span>
                          }
                        </TableCell>
                        <TableCell><UserActions u={u} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={usersPagination} itemLabel="users" />
              </Card>
            </TabsContent>

            {/* ===== ENGAGEMENT TAB ===== */}
            <TabsContent value="engagement">
              <AdminEngagementDashboard embedded />
            </TabsContent>

            {/* ===== LANDLORDS TAB ===== */}
            <TabsContent value="landlords">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
                    <CardTitle className="text-lg">Landlords</CardTitle>
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="Search landlords..." value={landlordSearch} onChange={(e) => setLandlordSearch(e.target.value)} className="pl-9" data-testid="admin-landlord-search" />
                    </div>
                    <Select value={landlordStatusFilter} onValueChange={setLandlordStatusFilter}>
                      <SelectTrigger className="w-full sm:w-40" data-testid="landlord-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="deactivated">Deactivated</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={landlordTypeFilter} onValueChange={setLandlordTypeFilter}>
                      <SelectTrigger className="w-full sm:w-40" data-testid="landlord-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="company">Company</SelectItem>
                      </SelectContent>
                    </Select>
                    {selectedLandlords.size > 0 && (
                      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center xl:ml-auto">
                        <span className="text-sm text-muted-foreground">{selectedLandlords.size} selected</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full sm:w-auto"
                          onClick={() => handleBulkApproveUsers(true)}
                          disabled={bulkApprovingLandlords || bulkDeletingLandlords}
                        >
                          {bulkApprovingLandlords ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                          Approve Selected
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="w-full sm:w-auto"
                          onClick={() => handleBulkDeactivateUsers(true)}
                          disabled={bulkApprovingLandlords || bulkDeletingLandlords}
                        >
                          {bulkDeletingLandlords ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                          Deactivate Selected
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={areAllVisibleLandlordsSelected}
                          onCheckedChange={handleToggleSelectAllLandlords}
                          aria-label="Select all landlords"
                        />
                      </TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Properties</TableHead>
                      <TableHead>Issues</TableHead>
                      <TableHead>Email Verified</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedLandlords.map(l => (
                      <TableRow key={l.id} className={l.is_active === false ? 'opacity-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedLandlords.has(l.id)}
                            onCheckedChange={() => toggleLandlordSelection(l.id)}
                            aria-label={`Select landlord ${l.name}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{l.name}</TableCell>
                        <TableCell className="text-sm">{l.email}</TableCell>
                        <TableCell>
                          {l.ownership_type === 'company'
                            ? <Badge className="bg-purple-100 text-purple-700">Company</Badge>
                            : <Badge className="bg-slate-100 text-slate-600">Individual</Badge>
                          }
                        </TableCell>
                        <TableCell>{l.property_count || 0}</TableCell>
                        <TableCell>{l.total_issues || 0}</TableCell>
                        <TableCell>
                          {l.is_email_verified
                            ? <Badge className="bg-green-100 text-green-700 gap-1"><CheckCircle className="h-3 w-3" />Verified</Badge>
                            : <Badge className="bg-yellow-100 text-yellow-700 gap-1"><AlertTriangle className="h-3 w-3" />Unverified</Badge>
                          }
                        </TableCell>
                        <TableCell>{getUserStatusBadge(l)}</TableCell>
                        <TableCell className="text-sm">{new Date(l.created_at).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell><UserActions u={l} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={landlordsPagination} itemLabel="landlords" />
              </Card>
            </TabsContent>

            {/* ===== ASSIGNMENTS TAB ===== */}
            <TabsContent value="assignments">
              <Card>
                <CardHeader>
                  <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
                    <CardTitle className="text-lg">Tenant Assignments</CardTitle>
                    <div className="relative flex-1 min-w-[220px]">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by tenant or property..."
                        value={assignmentSearch}
                        onChange={(e) => setAssignmentSearch(e.target.value)}
                        className="pl-9"
                        data-testid="admin-assignment-search"
                      />
                    </div>
                    <Select value={assignmentStatusFilter} onValueChange={setAssignmentStatusFilter}>
                      <SelectTrigger className="w-full sm:w-36" data-testid="assignment-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="ended">Ended</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap xl:ml-auto xl:w-auto">
                      {selectedAssignments.size > 0 && (
                        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                          <span className="text-sm text-muted-foreground">{selectedAssignments.size} selected</span>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="w-full sm:w-auto"
                            onClick={handleBulkDeleteAssignments}
                            disabled={bulkDeletingAssignments}
                          >
                            {bulkDeletingAssignments ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                            Delete Selected
                          </Button>
                        </div>
                      )}
                      <Button size="sm" className="w-full sm:w-auto" onClick={() => setCreateAssignment(true)} data-testid="create-assignment-btn">+ New Assignment</Button>
                    </div>
                  </div>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={areAllVisibleAssignmentsSelected}
                          onCheckedChange={handleToggleSelectAllAssignments}
                          aria-label="Select all assignments"
                        />
                      </TableHead>
                      <TableHead>Tenancy Participants</TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedAssignments.map(a => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedAssignments.has(a.id)}
                            onCheckedChange={() => toggleAssignmentSelection(a.id)}
                            aria-label={`Select assignment ${a.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="space-y-2">
                            {getAssignmentTenants(a).map((tenant, index) => (
                              <div key={tenant.user_id || tenant.email || `${a.id}-${index}`} className="rounded-md border border-border/60 px-3 py-2">
                                <div>{tenant.name || '-'}</div>
                                <div className="text-xs text-muted-foreground">{tenant.email || 'No email linked'}</div>
                              </div>
                            ))}
                            {getAssignmentTenants(a).length === 0 ? (
                              <div className="text-sm text-muted-foreground">No tenancy participants recorded</div>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{a.property_title} - {a.property_address}</TableCell>
                        <TableCell className="text-sm">{a.start_date}</TableCell>
                        <TableCell className="text-sm">{a.end_date || '-'}</TableCell>
                        <TableCell>
                          {a.status === 'active' ? <Badge className="bg-green-100 text-green-700">Active</Badge> : <Badge className="bg-gray-100 text-gray-600">Ended</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className={tableActionGroupClass}>
                            {a.status === 'active' && !a.tenancy_id && (
                              <Button className={tableActionButtonClass} size="sm" variant="outline" onClick={() => handleFinalizeAssignmentTenancy(a.id)} data-testid={`finalize-assignment-${a.id}`}>Finalize Tenancy</Button>
                            )}
                            <Button className={tableActionButtonClass} size="sm" variant="outline" onClick={() => openEditAssignment(a)} data-testid={`edit-assignment-${a.id}`}>Edit</Button>
                            {a.status === 'active' && (
                              <Button className={tableActionButtonClass} size="sm" variant="outline" onClick={() => handleEndAssignment(a.id)} data-testid={`end-assignment-${a.id}`}>End</Button>
                            )}
                            <Button className={tableActionButtonClass} size="sm" variant="destructive" onClick={() => handleDeleteAssignment(a.id)} data-testid={`delete-assignment-${a.id}`}>Delete</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredAssignments.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No assignments found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={assignmentsPagination} itemLabel="assignments" />
              </Card>
            </TabsContent>

            {/* ===== CONTACTS TAB ===== */}
            <TabsContent value="contacts">
              <Card>
                <CardHeader className="pb-2">
                  <div className="mb-2 flex flex-col gap-3 xl:flex-row xl:items-center">
                    <div className="relative flex-1 min-w-[220px]">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by name or email..."
                        value={contactSearch}
                        onChange={(e) => setContactSearch(e.target.value)}
                        className="pl-9"
                        data-testid="admin-contact-search"
                      />
                    </div>
                    <Select value={contactReadFilter} onValueChange={setContactReadFilter}>
                      <SelectTrigger className="w-full sm:w-32" data-testid="contact-read-filter"><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="unread">Unread</SelectItem>
                        <SelectItem value="read">Read</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => handleExportCsv(`${API_URL}/api/export/contacts?format=csv`, 'contacts', setExportingContacts)}
                      disabled={exportingContacts}
                    >
                      {exportingContacts ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                      Export CSV
                    </Button>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle className="text-lg">Contact Messages</CardTitle>
                    {selectedContacts.size > 0 && (
                      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                        <span className="text-sm text-muted-foreground">{selectedContacts.size} selected</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full sm:w-auto"
                          onClick={handleBulkMarkContactsRead}
                          disabled={bulkDeletingContacts || bulkMarkingContactsRead}
                        >
                          {bulkMarkingContactsRead ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                          Mark Selected Read
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="w-full sm:w-auto"
                          onClick={handleBulkDeleteContacts}
                          disabled={bulkDeletingContacts || bulkMarkingContactsRead}
                        >
                          {bulkDeletingContacts ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete Selected
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={areAllVisibleContactsSelected}
                          onCheckedChange={handleToggleSelectAllContacts}
                          aria-label="Select all messages"
                        />
                      </TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedContacts.map(c => (
                      <TableRow key={c.id} className={!c.is_read ? 'bg-blue-50/50 cursor-pointer' : 'cursor-pointer'} onClick={() => openContact(c.id)}>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedContacts.has(c.id)}
                            onCheckedChange={() => toggleContactSelection(c.id)}
                            aria-label={`Select message from ${c.name}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell className="text-sm">{c.email}</TableCell>
                        <TableCell className="max-w-[300px] text-sm break-words whitespace-pre-wrap">{c.message}</TableCell>
                        <TableCell className="text-sm">{new Date(c.created_at).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell>
                          {c.is_read ? <Badge variant="outline">Read</Badge> : <Badge className="bg-blue-100 text-blue-700">New</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className={tableActionGroupClass}>
                            {!c.is_read && (
                              <Button className={tableActionButtonClass} size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleMarkContactRead(c.id); }}>Mark Read</Button>
                            )}
                            <Button className={tableActionButtonClass} size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDeleteContact(c.id); }}>Delete</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={contactsPagination} itemLabel="messages" />
              </Card>
            </TabsContent>

            {/* ===== APPLICATIONS TAB ===== */}
            <TabsContent value="applications">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex flex-wrap gap-4 items-center">
                    <CardTitle className="text-lg">Applications</CardTitle>
                    <div className="relative flex-1 min-w-[220px]">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by tenant or property..."
                        value={applicationSearch}
                        onChange={(e) => setApplicationSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <Select value={applicationStatusFilter} onValueChange={setApplicationStatusFilter}>
                      <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm text-muted-foreground">Applications are pre-tenancy records only. Review them here, then use the Assignments tab to start email-based assignment or invite flow.</p>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedApplications.map(app => (
                      <TableRow key={app.id}>
                        <TableCell className="font-medium">
                          <div>{app.tenant_name || '-'}</div>
                          <div className="text-xs text-muted-foreground">{app.tenant_email || 'No email recorded'}</div>
                        </TableCell>
                        <TableCell className="text-sm">{app.property_title || '-'}</TableCell>
                        <TableCell>
                          {getApplicationDecisionStatus(app) === 'approved'
                            ? <Badge className="bg-green-100 text-green-700">Approved</Badge>
                            : getApplicationDecisionStatus(app) === 'rejected'
                            ? <Badge className="bg-red-100 text-red-700">Rejected</Badge>
                            : <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>}
                          {app.application_unread_count > 0 ? (
                            <Badge variant="outline" className="ml-2">{app.application_unread_count} unread</Badge>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-sm max-w-[240px] truncate">{getApplicationPreviewMessage(app)}</TableCell>
                        <TableCell className="text-sm">{app.created_at ? new Date(app.created_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                        <TableCell>
                          <div className={tableActionGroupClass}>
                            <Button className={tableActionButtonClass} size="sm" variant="outline" onClick={() => setSelectedApplication(app)}>View</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredApplications.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No applications found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={applicationsPagination} itemLabel="applications" />
              </Card>
            </TabsContent>

            {/* ===== NOTIFICATIONS TAB ===== */}

            {/* ===== DOCUMENT AUDIT TAB ===== */}
            <TabsContent value="audit">
              <AdminDocumentAuditTab />
            </TabsContent>

            {/* ===== DOCUMENT DELIVERY TAB ===== */}
            <TabsContent value="document-delivery">
              <AdminDocumentDeliveryTab />
            </TabsContent>

            <TabsContent value="compliance">
              <AdminComplianceTab />
            </TabsContent>

            {/* ===== ACTIVITY LOG TAB ===== */}
            <TabsContent value="activity">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex flex-wrap gap-4 items-center">
                    <CardTitle className="text-lg">Activity Log</CardTitle>
                    <div className="relative flex-1 min-w-[220px]">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by user or action..."
                        value={activitySearch}
                        onChange={(e) => setActivitySearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <Select value={activityRecencyFilter} onValueChange={setActivityRecencyFilter}>
                      <SelectTrigger className="w-40"><SelectValue placeholder="All time" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        <SelectItem value="7d">Last 7 Days</SelectItem>
                        <SelectItem value="30d">Last 30 Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedActivityLogs.map((log, idx) => (
                      <TableRow key={log.id || idx}>
                        <TableCell className="text-sm">{log.user_name || log.user_email || log.user_id || '-'}</TableCell>
                        <TableCell className="text-sm font-medium">{log.action_type || log.action || '-'}</TableCell>
                        <TableCell className="text-sm">{log.entity_type || log.entity || '-'}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{log.description || log.details || '-'}</TableCell>
                        <TableCell className="text-sm">{log.created_at ? new Date(log.created_at).toLocaleString('en-GB') : '-'}</TableCell>
                      </TableRow>
                    ))}
                    {filteredActivityLogs.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No activity logs found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={activityPagination} itemLabel="activity logs" />
              </Card>
            </TabsContent>

            {/* ===== MONITORING TAB ===== */}
            <TabsContent value="monitoring" className="mt-4 space-y-6">
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-1">Abuse &amp; Lifecycle Monitoring</h2>
                  <p className="text-sm text-muted-foreground">Aggregate signals across property/tenancy lifecycle events and document delivery abuse scoring.</p>
                </div>

                {/* Derived signal badges */}
                {lifecycleMonitoring && (lifecycleMonitoring.high_property_churn_signal || lifecycleMonitoring.high_tenancy_churn_signal ||
                  lifecycleMonitoring.total_property_blocked_actions > 0 || lifecycleMonitoring.total_tenancy_blocked_actions > 0 ||
                  lifecycleMonitoring.high_scraperapi_user_count > 0 || deliveryStats?.abuse?.suspicious_count > 0) && (
                  <div className="flex flex-wrap gap-3 mt-4">
                    {lifecycleMonitoring.total_property_blocked_actions > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                        ⚠ {lifecycleMonitoring.total_property_blocked_actions} property blocked action{lifecycleMonitoring.total_property_blocked_actions !== 1 ? 's' : ''}
                      </span>
                    )}
                    {lifecycleMonitoring.total_tenancy_blocked_actions > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                        ⚠ {lifecycleMonitoring.total_tenancy_blocked_actions} tenancy blocked action{lifecycleMonitoring.total_tenancy_blocked_actions !== 1 ? 's' : ''}
                      </span>
                    )}
                    {lifecycleMonitoring.high_property_churn_signal && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                        ↕ High property churn
                      </span>
                    )}
                    {lifecycleMonitoring.high_tenancy_churn_signal && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                        ↕ High tenancy churn
                      </span>
                    )}
                    {deliveryStats?.abuse?.suspicious_count > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-3 py-1 text-xs font-medium text-rose-800">
                        🚨 {deliveryStats.abuse.suspicious_count} suspicious deliveries
                      </span>
                    )}
                    {lifecycleMonitoring.high_scraperapi_user_count > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-800">
                        <Zap className="h-3 w-3" />
                        {lifecycleMonitoring.high_scraperapi_user_count} high ScraperAPI user{lifecycleMonitoring.high_scraperapi_user_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}

                {/* Lifecycle cards */}
                {lifecycleMonitoring ? (
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Property Lifecycle</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <dt className="text-muted-foreground">Created</dt>
                          <dd className="font-medium text-right">{lifecycleMonitoring.properties_created_count}</dd>
                          <dt className="text-muted-foreground">Archived</dt>
                          <dd className="font-medium text-right">{lifecycleMonitoring.properties_archived_count}</dd>
                          <dt className="text-muted-foreground">Delete attempts</dt>
                          <dd className="font-medium text-right">{lifecycleMonitoring.properties_delete_attempt_count}</dd>
                          <dt className="text-muted-foreground">Deleted</dt>
                          <dd className="font-medium text-right">{lifecycleMonitoring.properties_deleted_count}</dd>
                          <dt className={lifecycleMonitoring.properties_delete_blocked_count > 0 ? 'text-amber-700 font-medium' : 'text-muted-foreground'}>Delete blocked</dt>
                          <dd className={`font-medium text-right ${lifecycleMonitoring.properties_delete_blocked_count > 0 ? 'text-amber-700' : ''}`}>{lifecycleMonitoring.properties_delete_blocked_count}</dd>
                          <dt className={lifecycleMonitoring.properties_archived_mutation_block_count > 0 ? 'text-amber-700 font-medium' : 'text-muted-foreground'}>Archived freeze blocks</dt>
                          <dd className={`font-medium text-right ${lifecycleMonitoring.properties_archived_mutation_block_count > 0 ? 'text-amber-700' : ''}`}>{lifecycleMonitoring.properties_archived_mutation_block_count}</dd>
                        </dl>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Tenancy Lifecycle</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <dt className="text-muted-foreground">Portal created</dt>
                          <dd className="font-medium text-right">{lifecycleMonitoring.tenancies_created_count}</dd>
                          <dt className="text-muted-foreground">Manual created</dt>
                          <dd className="font-medium text-right">{lifecycleMonitoring.manual_tenancies_created_count}</dd>
                          <dt className="text-muted-foreground">Ended</dt>
                          <dd className="font-medium text-right">{lifecycleMonitoring.tenancies_ended_count}</dd>
                          <dt className="text-muted-foreground">Delete attempts</dt>
                          <dd className="font-medium text-right">{lifecycleMonitoring.tenancies_delete_attempt_count}</dd>
                          <dt className="text-muted-foreground">Deleted</dt>
                          <dd className="font-medium text-right">{lifecycleMonitoring.tenancies_deleted_count}</dd>
                          <dt className={lifecycleMonitoring.tenancies_delete_blocked_count > 0 ? 'text-amber-700 font-medium' : 'text-muted-foreground'}>Delete blocked</dt>
                          <dd className={`font-medium text-right ${lifecycleMonitoring.tenancies_delete_blocked_count > 0 ? 'text-amber-700' : ''}`}>{lifecycleMonitoring.tenancies_delete_blocked_count}</dd>
                          <dt className={lifecycleMonitoring.tenancies_ended_mutation_block_count > 0 ? 'text-amber-700 font-medium' : 'text-muted-foreground'}>Ended freeze blocks</dt>
                          <dd className={`font-medium text-right ${lifecycleMonitoring.tenancies_ended_mutation_block_count > 0 ? 'text-amber-700' : ''}`}>{lifecycleMonitoring.tenancies_ended_mutation_block_count}</dd>
                        </dl>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Document Delivery Abuse</CardTitle>
                        <p className="text-xs text-muted-foreground">Derived from delivery logs. Visit the <strong>Document Delivery</strong> tab for per-delivery detail.</p>
                      </CardHeader>
                      <CardContent>
                        {deliveryStats?.abuse ? (
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <dt className="text-muted-foreground">Suspicious deliveries (total)</dt>
                            <dd className={`font-medium text-right ${deliveryStats.abuse.suspicious_count > 0 ? 'text-rose-700' : ''}`}>{deliveryStats.abuse.suspicious_count}</dd>
                            <dt className="text-muted-foreground">Suspicious today</dt>
                            <dd className={`font-medium text-right ${deliveryStats.abuse.suspicious_today > 0 ? 'text-rose-700' : ''}`}>{deliveryStats.abuse.suspicious_today}</dd>
                            <dt className="text-muted-foreground">Rate-limited events</dt>
                            <dd className={`font-medium text-right ${deliveryStats.abuse.rate_limited_count > 0 ? 'text-amber-700' : ''}`}>{deliveryStats.abuse.rate_limited_count}</dd>
                            <dt className="text-muted-foreground">High abuse score (≥ 3)</dt>
                            <dd className={`font-medium text-right ${deliveryStats.abuse.high_abuse_score_count > 0 ? 'text-amber-700' : ''}`}>{deliveryStats.abuse.high_abuse_score_count}</dd>
                            <dt className="text-muted-foreground">Severe abuse score (≥ 5)</dt>
                            <dd className={`font-medium text-right ${deliveryStats.abuse.severe_abuse_score_count > 0 ? 'text-red-700 font-semibold' : ''}`}>{deliveryStats.abuse.severe_abuse_score_count}</dd>
                          </dl>
                        ) : (
                          <p className="text-sm text-muted-foreground">Loading delivery stats…</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Loading lifecycle metrics…</p>
                )}

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">High Abuse Users / Senders</CardTitle>
                    <p className="text-xs text-muted-foreground">Flagged across document delivery, property lifecycle, and tenancy lifecycle signals. Matched portal users can be suspended directly from this view.</p>
                  </CardHeader>
                  <CardContent>
                    {topAbuseSenders.length > 0 ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Sender</TableHead>
                              <TableHead>Matched account</TableHead>
                              <TableHead>Reasons</TableHead>
                              <TableHead>Signals</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {topAbuseSenders.map((row) => {
                              const matchedUser = row.matched_user;
                              const isSuspended = matchedUser?.is_active === false;
                              return (
                                <TableRow key={row.email}>
                                  <TableCell>
                                    <div className="space-y-1">
                                      <p className="font-medium text-foreground">{row.name || row.email}</p>
                                      <p className="text-xs text-muted-foreground">{row.email}</p>
                                      <p className="text-xs text-muted-foreground">Latest: {row.latest_delivery_at ? new Date(row.latest_delivery_at).toLocaleString('en-GB') : '—'}</p>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {matchedUser ? (
                                      <div className="space-y-2">
                                        <div>
                                          <p className="font-medium text-foreground">{matchedUser.name || matchedUser.email}</p>
                                          <p className="text-xs text-muted-foreground">{matchedUser.email}</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {getRoleBadge(matchedUser.role)}
                                          {getUserStatusBadge(matchedUser)}
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-sm text-muted-foreground">No matching portal account</p>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex flex-wrap gap-2">
                                      {(row.reasons || []).length > 0 ? row.reasons.map((reason) => {
                                        const badgeClass = reason.includes('Severe')
                                          ? 'bg-red-100 text-red-700 border-red-200'
                                          : reason.includes('rate limit')
                                            ? 'bg-amber-100 text-amber-800 border-amber-200'
                                            : reason.includes('churn') || reason.includes('lifecycle')
                                              ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
                                              : 'bg-rose-100 text-rose-700 border-rose-200';
                                        return <Badge key={reason} className={badgeClass}>{reason}</Badge>;
                                      }) : <span className="text-sm text-muted-foreground">—</span>}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="space-y-2 text-xs min-w-[170px]">
                                      <p className="text-muted-foreground font-medium uppercase tracking-wide text-[10px]">Delivery</p>
                                      <div className="grid grid-cols-2 gap-1.5">
                                        <div className="rounded border bg-muted/20 p-2">
                                          <p className="text-muted-foreground">Max score</p>
                                          <p className="font-semibold text-foreground">{row.max_abuse_score}</p>
                                        </div>
                                        <div className="rounded border bg-muted/20 p-2">
                                          <p className="text-muted-foreground">Total flagged</p>
                                          <p className="font-semibold text-foreground">{row.total_deliveries}</p>
                                        </div>
                                        <div className="rounded border bg-muted/20 p-2">
                                          <p className="text-muted-foreground">Suspicious</p>
                                          <p className="font-semibold text-foreground">{row.suspicious_deliveries}</p>
                                        </div>
                                        <div className="rounded border bg-muted/20 p-2">
                                          <p className="text-muted-foreground">Rate-limited</p>
                                          <p className="font-semibold text-foreground">{row.rate_limited_events}</p>
                                        </div>
                                      </div>
                                      {row.lifecycle_signals && (
                                        <>
                                          <p className="text-muted-foreground font-medium uppercase tracking-wide text-[10px] pt-1">Lifecycle</p>
                                          <div className="grid grid-cols-2 gap-1.5">
                                            <div className="rounded border bg-indigo-50/60 border-indigo-100 p-2">
                                              <p className="text-muted-foreground">Props created</p>
                                              <p className="font-semibold text-foreground">{row.lifecycle_signals.props_created}</p>
                                            </div>
                                            <div className={`rounded border p-2 ${row.lifecycle_signals.high_property_churn ? 'bg-red-50 border-red-200' : 'bg-indigo-50/60 border-indigo-100'}`}>
                                              <p className="text-muted-foreground">Props ended</p>
                                              <p className="font-semibold text-foreground">{row.lifecycle_signals.props_ended}</p>
                                            </div>
                                            <div className="rounded border bg-indigo-50/60 border-indigo-100 p-2">
                                              <p className="text-muted-foreground">Tenancies</p>
                                              <p className="font-semibold text-foreground">{row.lifecycle_signals.tenancies_created}</p>
                                            </div>
                                            <div className={`rounded border p-2 ${row.lifecycle_signals.high_tenancy_churn ? 'bg-red-50 border-red-200' : 'bg-indigo-50/60 border-indigo-100'}`}>
                                              <p className="text-muted-foreground">Tenancies ended</p>
                                              <p className="font-semibold text-foreground">{row.lifecycle_signals.tenancies_ended}</p>
                                            </div>
                                            {row.lifecycle_signals.total_blocked_actions > 0 && (
                                              <div className="col-span-2 rounded border bg-amber-50 border-amber-200 p-2">
                                                <p className="text-muted-foreground">Blocked actions</p>
                                                <p className="font-semibold text-foreground">{row.lifecycle_signals.total_blocked_actions}</p>
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {matchedUser ? (
                                      <div className="flex justify-end">
                                        <Button
                                          size="sm"
                                          variant={isSuspended ? 'outline' : 'destructive'}
                                          onClick={() => openMonitoringSuspendConfirm(matchedUser, row.reasons || [])}
                                        >
                                          {isSuspended ? 'Unsuspend' : 'Suspend'}
                                        </Button>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">No action available</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    ) : deliveryStats?.abuse ? (
                      <p className="text-sm text-muted-foreground">No high-abuse senders currently flagged.</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">Loading abuse users…</p>
                    )}
                  </CardContent>
                </Card>

                {/* ── Storage Usage ───────────────────────────────────────── */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Storage Usage — Per Landlord</CardTitle>
                    {storageMonitoring?.totals && (
                      <div className="flex flex-wrap gap-4 mt-1 text-xs text-muted-foreground">
                        <span>Total across all landlords: <strong className="text-foreground">{formatBytes(storageMonitoring.totals.all_storage_bytes)}</strong></span>
                        <span>Total files: <strong className="text-foreground">{storageMonitoring.totals.all_file_count.toLocaleString()}</strong></span>
                        <span>Landlords with storage: <strong className="text-foreground">{storageMonitoring.totals.landlord_count}</strong></span>
                        <span>Avg per landlord: <strong className="text-foreground">{formatBytes(storageMonitoring.totals.avg_storage_bytes)}</strong></span>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="p-0">
                    {storageMonitoring?.landlords?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/40">
                              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Landlord</th>
                              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Total</th>
                              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Files</th>
                              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Uploaded</th>
                              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Generated</th>
                              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Compliance</th>
                              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Tenancy</th>
                              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Financial</th>
                              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Growth 30d</th>
                            </tr>
                          </thead>
                          <tbody>
                            {storageMonitoring.landlords.map((row, idx) => {
                              const isTop = idx < 3;
                              const isHighGrowth = storageMonitoring.totals.avg_storage_bytes > 0 &&
                                row.storage.growth_30d_bytes > storageMonitoring.totals.avg_storage_bytes * 0.5;
                              return (
                                <tr key={row.landlord_id} className={`border-b last:border-0 ${isTop ? 'bg-amber-50/50' : ''}`}>
                                  <td className="px-4 py-2">
                                    <div className="flex items-center gap-1.5">
                                      {isTop && <span className="text-xs text-amber-600 font-bold">#{idx + 1}</span>}
                                      <div>
                                        <p className="font-medium leading-tight">{row.landlord_name || 'Unknown'}</p>
                                        <p className="text-xs text-muted-foreground">{row.landlord_email}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={`px-4 py-2 text-right font-semibold ${isTop ? 'text-amber-700' : ''}`}>{formatBytes(row.storage.total_bytes)}</td>
                                  <td className="px-4 py-2 text-right text-muted-foreground">{row.storage.file_count.toLocaleString()}</td>
                                  <td className="px-4 py-2 text-right">{formatBytes(row.storage.uploaded_bytes)}</td>
                                  <td className="px-4 py-2 text-right">{formatBytes(row.storage.generated_bytes)}</td>
                                  <td className="px-4 py-2 text-right">{formatBytes(row.storage.compliance_bytes)}</td>
                                  <td className="px-4 py-2 text-right">{formatBytes(row.storage.tenancy_bytes)}</td>
                                  <td className="px-4 py-2 text-right">{formatBytes(row.storage.financial_bytes)}</td>
                                  <td className={`px-4 py-2 text-right ${isHighGrowth ? 'text-rose-600 font-medium' : ''}`}>
                                    {row.storage.growth_30d_bytes > 0 ? `+${formatBytes(row.storage.growth_30d_bytes)}` : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : storageMonitoring ? (
                      <p className="text-sm text-muted-foreground px-4 py-4">No storage data recorded yet.</p>
                    ) : (
                      <p className="text-sm text-muted-foreground px-4 py-4">Loading storage data…</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ===== HEALTH CHECK AUDIT TAB ===== */}
            <TabsContent value="health-check-audit">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Health Check Email Audit</CardTitle>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipient Email</TableHead>
                      <TableHead>User ID</TableHead>
                      <TableHead>Send Status</TableHead>
                      <TableHead>TOS</TableHead>
                      <TableHead>Accepted At</TableHead>
                      <TableHead>Created At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedHealthCheckAudit.map((entry, idx) => (
                      <TableRow key={entry.id || idx}>
                        <TableCell className="text-sm">{entry.recipient_email || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{entry.user_id || 'Unauthenticated'}</TableCell>
                        <TableCell>
                          {entry.send_status === 'sent'
                            ? <Badge className="bg-green-100 text-green-700">Sent</Badge>
                            : entry.send_status === 'failed'
                            ? <Badge className="bg-red-100 text-red-700">Failed</Badge>
                            : <Badge variant="outline">{entry.send_status || '-'}</Badge>}
                        </TableCell>
                        <TableCell className="text-sm">{entry.tos_version_accepted || '-'}</TableCell>
                        <TableCell className="text-sm">{entry.tos_accepted_at ? new Date(entry.tos_accepted_at).toLocaleString('en-GB') : '-'}</TableCell>
                        <TableCell className="text-sm">{entry.created_at ? new Date(entry.created_at).toLocaleString('en-GB') : '-'}</TableCell>
                      </TableRow>
                    ))}
                    {healthCheckAudit.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No health check emails found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                <AdminTabPagination pagination={healthCheckAuditPagination} itemLabel="health check emails" />
              </Card>
            </TabsContent>

            {/* ===== EMAIL LOGS TAB ===== */}
            <TabsContent value="email-logs">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
                      <CardTitle className="text-lg shrink-0">Email Logs</CardTitle>
                      <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 text-sm md:grid-cols-3">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">Current working</p>
                          <p className="font-semibold">{emailProviderSettingsLoading ? 'Loading...' : currentWorkingProviderLabel}</p>
                          <p className="text-xs text-muted-foreground">
                            {emailProviderSettings?.current_working_provider_source === 'last_successful_email'
                              ? `Based on the latest successful send${emailProviderSettings?.current_working_provider_at ? ` • ${new Date(emailProviderSettings.current_working_provider_at).toLocaleString('en-GB')}` : ''}`
                              : 'Based on current provider availability and preference'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">Preferred priority</p>
                          <p className="font-semibold">{emailProviderSettingsLoading ? 'Loading...' : preferredEmailProviderLabel}</p>
                          <p className="text-xs text-muted-foreground">This only changes which provider is tried first. Fallback stays intact.</p>
                        </div>
                        <div className="flex items-center justify-between gap-3 rounded-md border bg-background p-3">
                          <div>
                            <p className="font-medium">Prefer Brevo first</p>
                            <p className="text-xs text-muted-foreground">Off keeps Resend as the default first try.</p>
                          </div>
                          <Switch
                            checked={emailProviderSettings?.preferred_email_provider === 'brevo'}
                            onCheckedChange={handlePreferredEmailProviderChange}
                            disabled={emailProviderSettingsLoading || emailProviderUpdating}
                            aria-label="Prefer Brevo as the first email provider"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by recipient, subject or sender..."
                        value={emailLogSearch}
                        onChange={(e) => setEmailLogSearch(e.target.value)}
                        className="pl-9"
                        data-testid="email-log-search"
                      />
                    </div>
                    <Select value={emailLogStatusFilter} onValueChange={setEmailLogStatusFilter}>
                      <SelectTrigger className="w-full sm:w-40" data-testid="email-log-status-filter">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="mocked">Mocked</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={emailLogTypeFilter} onValueChange={setEmailLogTypeFilter}>
                      <SelectTrigger className="w-full sm:w-48" data-testid="email-log-type-filter">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="email_verification">Email Verification</SelectItem>
                        <SelectItem value="password_reset">Password Reset</SelectItem>
                        <SelectItem value="password_changed">Password Changed</SelectItem>
                        <SelectItem value="email_change">Email Change</SelectItem>
                        <SelectItem value="tenant_welcome">Tenant Welcome</SelectItem>
                        <SelectItem value="rent_receipt">Rent Receipt</SelectItem>
                        <SelectItem value="rent_reminder">Rent Reminder</SelectItem>
                        <SelectItem value="issue_notification">Issue Notification</SelectItem>
                        <SelectItem value="health_check_results">Health Check</SelectItem>
                        <SelectItem value="document_delivery">Document Delivery</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>To</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sent At</TableHead>
                        <TableHead>Created At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedEmailLogs.map((log, idx) => (
                        <TableRow key={log.id || idx}>
                          <TableCell className="text-sm font-medium max-w-[160px] truncate" title={log.to_email}>{log.to_email || '-'}</TableCell>
                          <TableCell className="text-sm max-w-[220px] truncate" title={log.subject}>{log.subject || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate" title={log.from_email}>{log.from_email || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs whitespace-nowrap">
                              {log.email_type || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs whitespace-nowrap">
                              {log.email_provider === 'brevo' ? 'Brevo' : log.email_provider === 'resend' ? 'Resend' : log.email_provider === 'mocked' ? 'Mocked' : '—'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {log.status === 'sent'
                              ? <Badge className="bg-green-100 text-green-700 gap-1"><CheckCircle className="h-3 w-3" />Sent</Badge>
                              : log.status === 'mocked'
                              ? <Badge className="bg-blue-100 text-blue-700">Mocked</Badge>
                              : log.status === 'failed'
                              ? <Badge className="bg-red-100 text-red-700 gap-1" title={log.error}><AlertTriangle className="h-3 w-3" />Failed</Badge>
                              : <Badge variant="outline">{log.status || '-'}</Badge>}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {log.sent_at ? new Date(log.sent_at).toLocaleString('en-GB') : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {log.created_at ? new Date(log.created_at).toLocaleString('en-GB') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredEmailLogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                            {emailLogsLoading ? 'Loading...' : 'No email logs found'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                <AdminTabPagination pagination={emailLogsPagination} itemLabel="emails" />
              </Card>
            </TabsContent>

            {/* ===== SIGNING TAB ===== */}
            <TabsContent value="signing">
              <AdminSigningTab
                versions={signingVersions}
                loading={refreshing || loading}
                onRefresh={() => fetchData(0, true)}
              />
            </TabsContent>

            {/* ===== MARKET RATE HISTORY TAB ===== */}
            <TabsContent value="market-rate-history">
              <AdminMarketRateHistoryTab revision={marketRateRevision} />
            </TabsContent>

            {/* ===== API USAGE TAB ===== */}
            <TabsContent value="api-usage" className="mt-4 space-y-6">
              {(() => {
                const daysRemaining = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate() - new Date().getDate();
                const pctUsed = apiUsage?.scraperapi?.pct_used ?? 0;
                const usageColor = pctUsed > 90 ? 'text-red-700' : pctUsed >= 70 ? 'text-amber-700' : 'text-green-700';
                const barColor = pctUsed > 90 ? 'bg-red-500' : pctUsed >= 70 ? 'bg-amber-500' : 'bg-green-500';
                return (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground mb-1">API Usage Monitoring</h2>
                      <p className="text-sm text-muted-foreground">Track outbound ScraperAPI, Gemini, and Google Street View calls against plan limits.</p>
                    </div>

                    {apiUsageLoading && !apiUsage ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-navy-600" />
                      </div>
                    ) : (
                      <>
                        {/* Summary cards */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          <Card>
                            <CardContent className="p-4">
                              <p className="text-xs text-muted-foreground mb-1">ScraperAPI used</p>
                              <p className={`text-2xl font-bold ${usageColor}`}>
                                {apiUsage?.scraperapi?.current_month_total ?? 0}
                                <span className="text-base font-normal text-muted-foreground"> / {apiUsage?.scraperapi?.monthly_limit ?? 1000}</span>
                              </p>
                              <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(pctUsed, 100)}%` }} />
                              </div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="p-4">
                              <p className="text-xs text-muted-foreground mb-1">ScraperAPI %</p>
                              <p className={`text-2xl font-bold ${usageColor}`}>{pctUsed.toFixed(1)}%</p>
                              <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(pctUsed, 100)}%` }} />
                              </div>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="p-4">
                              <p className="text-xs text-muted-foreground mb-1">Gemini calls</p>
                              <p className="text-2xl font-bold text-foreground">{apiUsage?.gemini?.current_month_total ?? 0}</p>
                              <p className="text-xs text-muted-foreground mt-1">this month</p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="p-4">
                              <p className="text-xs text-muted-foreground mb-1">Street View calls</p>
                              <p className="text-2xl font-bold text-foreground">{apiUsage?.google_street_view?.current_month_total ?? 0}</p>
                              <p className="text-xs text-muted-foreground mt-1">this month</p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="p-4">
                              <p className="text-xs text-muted-foreground mb-1">Days remaining</p>
                              <p className="text-2xl font-bold text-foreground">{daysRemaining}</p>
                              <p className="text-xs text-muted-foreground mt-1">in current month</p>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Daily breakdown charts */}
                        {(() => {
                          const scraperData = apiUsage?.scraperapi?.daily_breakdown || [];
                          const geminiData = apiUsage?.gemini?.daily_breakdown || [];
                          const svData = apiUsage?.google_street_view?.daily_breakdown || [];
                          const fmtDate = (d) => {
                            const parts = d.split('-');
                            return `${parts[2]}`.replace(/^0/, '') + '/' + parts[1];
                          };
                          const scraperChartData = scraperData.map(d => ({ date: fmtDate(d.date), count: d.count }));
                          const geminiChartData = geminiData.map(d => ({ date: fmtDate(d.date), count: d.count }));
                          const svChartData = svData.map(d => ({ date: fmtDate(d.date), count: d.count }));
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <Card>
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm font-semibold">ScraperAPI — Daily Calls</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {scraperChartData.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">No data</p>
                                  ) : (
                                    <ResponsiveContainer width="100%" height={200}>
                                      <BarChart data={scraperChartData} margin={{ left: -20, right: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                        <ChartTooltip />
                                        <Bar dataKey="count" name="Calls" fill="#3b82f6" />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  )}
                                </CardContent>
                              </Card>
                              <Card>
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm font-semibold">Gemini — Daily Calls</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {geminiChartData.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">No data</p>
                                  ) : (
                                    <ResponsiveContainer width="100%" height={200}>
                                      <BarChart data={geminiChartData} margin={{ left: -20, right: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                        <ChartTooltip />
                                        <Bar dataKey="count" name="Calls" fill="#8b5cf6" />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  )}
                                </CardContent>
                              </Card>
                              <Card className="md:col-span-2">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm font-semibold">Street View — Daily Calls</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {svChartData.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">No data</p>
                                  ) : (
                                    <ResponsiveContainer width="100%" height={200}>
                                      <BarChart data={svChartData} margin={{ left: -20, right: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                        <ChartTooltip />
                                        <Bar dataKey="count" name="Calls" fill="#10b981" />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  )}
                                </CardContent>
                              </Card>
                            </div>
                          );
                        })()}

                        {/* Filters */}
                        <div className="flex flex-wrap gap-3 items-center">
                          <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <input
                              type="text"
                              placeholder="Search by location, user or operation…"
                              value={apiUsageSearch}
                              onChange={e => setApiUsageSearch(e.target.value)}
                              className="pl-8 pr-3 py-2 text-sm border rounded-md w-full bg-background"
                            />
                          </div>
                          <select
                            value={apiUsageServiceFilter}
                            onChange={e => setApiUsageServiceFilter(e.target.value)}
                            className="text-sm border rounded-md px-3 py-2 bg-background"
                          >
                            <option value="all">All Services</option>
                            <option value="scraperapi">ScraperAPI</option>
                            <option value="gemini">Gemini</option>
                            <option value="google_street_view">Street View</option>
                          </select>
                        </div>

                        {/* Log table */}
                        <Card>
                          <CardContent className="p-0">
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Timestamp</TableHead>
                                    <TableHead>Service</TableHead>
                                    <TableHead>Operation</TableHead>
                                    <TableHead>Location</TableHead>
                                    <TableHead>User</TableHead>
                                    <TableHead>Credits</TableHead>
                                    <TableHead>Results</TableHead>
                                    <TableHead>Status</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {paginatedApiUsageLogs.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                        No API calls recorded
                                      </TableCell>
                                    </TableRow>
                                  ) : paginatedApiUsageLogs.map((log, idx) => (
                                    <TableRow key={log._id || `${log.timestamp}-${idx}`}>
                                      <TableCell className="text-xs">{new Date(log.timestamp).toLocaleString('en-GB')}</TableCell>
                                      <TableCell>
                                        {log.service === 'scraperapi' ? (
                                          <Badge variant="outline">ScraperAPI</Badge>
                                        ) : log.service === 'gemini' ? (
                                          <Badge variant="outline">Gemini</Badge>
                                        ) : log.service === 'google_street_view' ? (
                                          <Badge variant="outline">Street View</Badge>
                                        ) : (
                                          <Badge variant="outline">{log.service}</Badge>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-xs">{log.operation}</TableCell>
                                      <TableCell className="text-xs">{log.location || '—'}</TableCell>
                                      <TableCell className="text-xs">{log.triggered_by_email || 'Background job'}</TableCell>
                                      <TableCell className="text-xs">{log.credits_used}</TableCell>
                                      <TableCell className="text-xs">{log.result_count ?? '—'}</TableCell>
                                      <TableCell>
                                        {log.success ? (
                                          <Badge className="bg-green-100 text-green-700 text-xs">OK</Badge>
                                        ) : (
                                          <Badge className="bg-red-100 text-red-700 text-xs">Failed</Badge>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                            <div className="p-4">
                              <AdminTabPagination pagination={apiUsageLogsPagination} itemLabel="API calls" />
                            </div>
                          </CardContent>
                        </Card>
                      </>
                    )}
                  </div>
                );
              })()}
            </TabsContent>
          </Tabs>
          </div>
        </div>

        {/* ===== ISSUE DETAIL/EDIT DIALOG ===== */}
        <Dialog open={!!selectedIssue} onOpenChange={() => setSelectedIssue(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="issue-edit-dialog">
            <DialogHeader>
              <DialogTitle>{selectedIssue?.title}</DialogTitle>
              <DialogDescription>Reported by {selectedIssue?.tenant_name}</DialogDescription>
            </DialogHeader>
            {selectedIssue && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {getStatusBadge(selectedIssue.status)}
                  {getPriorityBadge(selectedIssue.priority)}
                </div>
                {selectedIssue.property_title && (
                  <p className="text-sm"><span className="text-muted-foreground">Property:</span> <strong>{selectedIssue.property_title}</strong> - {selectedIssue.property_address}</p>
                )}
                <div className="p-4 bg-navy-50 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{selectedIssue.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Status</label>
                    <Select value={selectedIssue.status} onValueChange={(val) => handleUpdateIssue(selectedIssue.id, val)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reported">Reported</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Priority</label>
                    <Select value={selectedIssue.priority} onValueChange={(val) => {
                      setUpdatingIssue(true);
                      axios.put(`${API_URL}/api/issues/${selectedIssue.id}`, { priority: val })
                        .then(() => { toast.success('Priority updated'); setSelectedIssue(null); handleSearchIssues(issueSearch); })
                        .catch(() => toast.error('Failed to update'))
                        .finally(() => setUpdatingIssue(false));
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Admin Notes</label>
                  <Textarea placeholder="Add notes..." value={issueNotes} onChange={(e) => setIssueNotes(e.target.value)} rows={3} />
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => handleUpdateIssue(selectedIssue.id, selectedIssue.status, issueNotes)} disabled={updatingIssue}>
                    {updatingIssue ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save Notes
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Created: {new Date(selectedIssue.created_at).toLocaleString('en-GB')}</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ===== CONTACT DETAIL DIALOG ===== */}
        <Dialog open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
          <DialogContent className="max-w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="contact-detail-dialog">
            <DialogHeader>
              <DialogTitle>Message from {selectedContact?.name}</DialogTitle>
              <DialogDescription className="truncate">{selectedContact?.email}</DialogDescription>
            </DialogHeader>
            {selectedContact && (
              <div className="space-y-4">
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Sender</div>
                  <div className="text-base font-medium">{selectedContact.name} <span className="text-sm text-muted-foreground">• {selectedContact.email}</span></div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground">Subject</div>
                  <div className="text-sm font-medium">{selectedContact.subject}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground">Message</div>
                  <div
                    className="p-4 bg-navy-50 rounded-lg text-sm whitespace-pre-wrap break-words overflow-hidden"
                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >{selectedContact.message}</div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">Replies</div>
                    <div className="text-xs text-muted-foreground">{(selectedContact.replies || []).length} replies</div>
                  </div>
                  <div className="space-y-4 mt-2">
                    {(selectedContact.replies || []).length === 0 && (
                      <p className="text-sm text-muted-foreground">No replies yet</p>
                    )}
                    {(selectedContact.replies || []).map((r, idx) => (
                      <div key={idx} className="p-4 bg-white border rounded">
                        <div className="flex items-start justify-between">
                          <div className="text-sm font-medium">{r.author}</div>
                          <div className="text-xs text-muted-foreground ml-4">{new Date(r.timestamp).toLocaleString('en-GB')}</div>
                        </div>
                        <div
                          className="text-sm whitespace-pre-wrap break-words mt-2 overflow-hidden"
                          style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                        >{r.text}</div>
                      </div>
                    ))}
                    <div ref={repliesEndRef} />
                  </div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-2">Write a reply</div>
                  <Textarea
                    rows={3}
                    value={contactReply}
                    onChange={(e) => setContactReply(e.target.value)}
                    onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    className="resize-none"
                    placeholder="Write your reply here..."
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); handleReplyToContact(); }} disabled={replying || !contactReply.trim()}>{replying ? 'Sending...' : 'Send Reply'}</Button>
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleSuggestContactReply(); }} disabled={suggestingReply}>
                      <Sparkles className="h-3.5 w-3.5 mr-1" />{suggestingReply ? 'Suggesting...' : 'Suggest with AI'}
                    </Button>
                    {!selectedContact.is_read && (
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleMarkContactRead(selectedContact.id); setSelectedContact(null); }}>Mark Read</Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDeleteContact(selectedContact.id); }}>Delete</Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedContact(null)}>Close</Button>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">Created: {new Date(selectedContact.created_at).toLocaleString('en-GB')}</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ===== EDIT USER DIALOG ===== */}
        <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update details for {editUser?.name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* ===== ACCOUNT ===== */}
              <div className="space-y-4">
                <div className="pb-2 border-b">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase">Account</h4>
                </div>
                <div>
                  <label className="text-sm font-medium">Email (Login & Receipt)</label>
                  <Input type="email" value={editUserForm.email || ''} onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})} data-testid="edit-user-email" />
                </div>
                <div>
                  <label className="text-sm font-medium">Role</label>
                  <Select value={editUserForm.role || ''} onValueChange={(val) => setEditUserForm({
                    ...editUserForm,
                    role: val,
                    ownership_type: val === 'landlord' ? (editUserForm.ownership_type || 'individual') : 'individual',
                    company_name: val === 'landlord' ? editUserForm.company_name : '',
                    company_registration: val === 'landlord' ? editUserForm.company_registration : '',
                  })}>
                    <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="landlord">Landlord</SelectItem>
                      <SelectItem value="tenant">Tenant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* ===== ISSUER INFORMATION (Landlords only) ===== */}
              {editUserForm.role === 'landlord' && (
                <Card className="bg-muted/30">
                  <CardContent className="pt-6 space-y-4">
                    <div className="pb-2 border-b">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase">Issuer Information</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">This information appears on rent receipts</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Ownership Type</label>
                      <Select
                        value={editUserForm.ownership_type || 'individual'}
                        onValueChange={(val) => {
                          setAdminUserErrors((prev) => ({ ...prev, edit_company_name: '', edit_company_registration: '' }));
                          setEditUserForm({
                            ...editUserForm,
                            ownership_type: val,
                            company_name: val === 'company' ? editUserForm.company_name : '',
                            company_registration: val === 'company' ? editUserForm.company_registration : '',
                          });
                        }}
                      >
                        <SelectTrigger data-testid="edit-user-ownership-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="individual">Individual</SelectItem>
                          <SelectItem value="company">Company</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Full Name</label>
                      <Input value={editUserForm.name || ''} onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})} placeholder="Full Name (e.g. John Smith)" data-testid="edit-user-name" />
                    </div>
                    {editUserForm.ownership_type === 'company' && (
                      <>
                        <div>
                          <label className="text-sm font-medium">Company Name</label>
                          <Input value={editUserForm.company_name || ''} onChange={(e) => {
                            setEditUserForm({...editUserForm, company_name: e.target.value});
                            setAdminUserErrors((prev) => ({ ...prev, edit_company_name: '' }));
                          }} placeholder="Company name" data-testid="edit-user-company-name" />
                          {adminUserErrors.edit_company_name ? <p className="mt-1 text-xs text-destructive">{adminUserErrors.edit_company_name}</p> : null}
                        </div>
                      </>
                    )}
                    <div>
                      <label className="text-sm font-medium">Business Address (Optional)</label>
                      <Input value={editUserForm.business_address || ''} onChange={(e) => setEditUserForm({...editUserForm, business_address: e.target.value})} placeholder="Business address" data-testid="edit-user-business-address" />
                    </div>
                    {editUserForm.ownership_type === 'company' && (
                      <div>
                        <label className="text-sm font-medium">Company Registration</label>
                        <Input value={editUserForm.company_registration || ''} onChange={(e) => {
                          setEditUserForm({...editUserForm, company_registration: e.target.value});
                          setAdminUserErrors((prev) => ({ ...prev, edit_company_registration: '' }));
                        }} placeholder="Company registration" data-testid="edit-user-company-registration" />
                        {adminUserErrors.edit_company_registration ? <p className="mt-1 text-xs text-destructive">{adminUserErrors.edit_company_registration}</p> : null}
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium">VAT Number (Optional)</label>
                      <Input value={editUserForm.vat_number || ''} onChange={(e) => setEditUserForm({...editUserForm, vat_number: e.target.value})} placeholder="VAT number" data-testid="edit-user-vat-number" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Phone (Optional)</label>
                      <Input value={editUserForm.phone || ''} onChange={(e) => setEditUserForm({...editUserForm, phone: e.target.value})} placeholder="Contact phone" data-testid="edit-user-phone" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Website (Optional)</label>
                      <Input value={editUserForm.website || ''} onChange={(e) => setEditUserForm({...editUserForm, website: e.target.value})} placeholder="https://..." data-testid="edit-user-website" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* ===== NON-LANDLORD USER INFO ===== */}
              {editUserForm.role !== 'landlord' && (
                <div className="space-y-4">
                  <div className="pb-2 border-b">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase">Personal Information</h4>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Full Name</label>
                    <Input value={editUserForm.name || ''} onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})} placeholder="Full Name (e.g. John Smith)" data-testid="edit-user-name" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Phone (Optional)</label>
                    <Input value={editUserForm.phone || ''} onChange={(e) => setEditUserForm({...editUserForm, phone: e.target.value})} placeholder="Phone number" data-testid="edit-user-phone" />
                  </div>
                </div>
              )}

              {/* ===== ACCOUNT STATUS ===== */}
              <div className="space-y-4">
                <div className="pb-2 border-b">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase">Account Status</h4>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <label className="text-sm font-medium">Account Active</label>
                    <p className="text-xs text-muted-foreground">Deactivated users cannot log in</p>
                  </div>
                  <input type="checkbox" className="h-4 w-4 rounded" checked={editUserForm.is_active !== false}
                    onChange={(e) => setEditUserForm({...editUserForm, is_active: e.target.checked})} data-testid="edit-user-active-toggle" />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <label className="text-sm font-medium">Approved</label>
                    <p className="text-xs text-muted-foreground">Required for landlords to access the platform</p>
                  </div>
                  <input type="checkbox" className="h-4 w-4 rounded" checked={editUserForm.is_approved !== false}
                    onChange={(e) => setEditUserForm({...editUserForm, is_approved: e.target.checked})} data-testid="edit-user-approved-toggle" />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
                <Button onClick={handleSaveUser} disabled={saving} data-testid="save-user-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== RESET PASSWORD DIALOG ===== */}
        <Dialog open={!!resetPwUser} onOpenChange={() => setResetPwUser(null)}>
          <DialogContent data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>Set a new password for {resetPwUser?.name} ({resetPwUser?.email})</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">New Password</label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters" data-testid="reset-pw-input" />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setResetPwUser(null)}>Cancel</Button>
                <Button onClick={handleResetPassword} disabled={saving} data-testid="reset-pw-submit">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Reset Password
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== EDIT PROPERTY DIALOG ===== */}
        <Dialog open={!!editProperty} onOpenChange={() => setEditProperty(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-property-dialog">
            <DialogHeader>
              <DialogTitle>Edit Property</DialogTitle>
              <DialogDescription>Update details for {editProperty?.title}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Address + Title */}
              <div>
                <label className="text-sm font-medium">Address</label>
                <div className="space-y-2">
                  <AddressInput
                    defaultValue={editPropertyForm.address || ''}
                    onAddressSelect={(addressObject) => {
                      setEditPropertyForm((prev) => ({
                        ...prev,
                        address: Object.prototype.hasOwnProperty.call(addressObject || {}, 'formatted')
                          ? (addressObject?.formatted ?? '')
                          : prev.address,
                        postcode: addressObject?.postcode || prev.postcode,
                      }));
                    }}
                    data-testid="edit-property-address"
                  />
                  {(editPropertyForm.address || '').trim() && (
                    <p className="text-xs text-muted-foreground">{editPropertyForm.address}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Property Name</label>
                <Input value={editPropertyForm.title || ''} onChange={(e) => setEditPropertyForm({...editPropertyForm, title: e.target.value})} data-testid="edit-property-title" />
              </div>

              {/* Postcode + Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Postcode</label>
                  <Input value={editPropertyForm.postcode || ''} onChange={(e) => setEditPropertyForm({...editPropertyForm, postcode: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Property Type</label>
                  <Select value={editPropertyForm.property_type || ''} onValueChange={(val) => setEditPropertyForm({...editPropertyForm, property_type: val})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat</SelectItem>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="studio">Studio</SelectItem>
                      <SelectItem value="bungalow">Bungalow</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Bedrooms + Bathrooms */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Bedrooms</label>
                  <Select value={editPropertyForm.bedrooms || ''} onValueChange={(val) => setEditPropertyForm({...editPropertyForm, bedrooms: val})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Bathrooms</label>
                  <Select value={editPropertyForm.bathrooms || ''} onValueChange={(val) => setEditPropertyForm({...editPropertyForm, bathrooms: val})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Rent */}
              <div>
                <label className="text-sm font-medium">Monthly Rent (£)</label>
                <Input type="number" placeholder="e.g. 1500" value={editPropertyForm.rent || ''} onChange={(e) => setEditPropertyForm({...editPropertyForm, rent: parseFloat(e.target.value) || 0})} min="0" step="0.01" />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  placeholder="Property notes..."
                  value={editPropertyForm.description || ''}
                  onChange={(e) => setEditPropertyForm({...editPropertyForm, description: e.target.value})}
                  rows={3}
                />
              </div>

              {/* Occupied + Status */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="edit-property-is-occupied"
                    checked={Boolean(editPropertyForm.is_occupied)}
                    onChange={(e) => setEditPropertyForm({...editPropertyForm, is_occupied: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="edit-property-is-occupied" className="text-sm font-medium cursor-pointer">Property Occupied</label>
                </div>
                <p className="text-xs text-muted-foreground pl-7">Occupied properties are treated as <strong>No listing</strong> until marked unoccupied.</p>
              </div>

              {/* Property MOT */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-4 text-sm">Property MOT Information</h4>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="edit-has-gas-supply"
                      checked={Boolean(editPropertyForm.has_gas_supply)}
                      onChange={(e) => setEditPropertyForm({...editPropertyForm, has_gas_supply: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="edit-has-gas-supply" className="text-sm font-medium cursor-pointer">Has Gas Supply</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="edit-furnished-appliances"
                      checked={Boolean(editPropertyForm.furnished_appliances)}
                      onChange={(e) => setEditPropertyForm({...editPropertyForm, furnished_appliances: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="edit-furnished-appliances" className="text-sm font-medium cursor-pointer">Furnished with Electric Appliances</label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Number of Floors</label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="e.g. 3"
                      value={editPropertyForm.floors || ''}
                      onChange={(e) => setEditPropertyForm({...editPropertyForm, floors: normalizeNonNegativeIntegerInput(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Furnishing Type</label>
                    <Select value={editPropertyForm.furnishing || 'none'} onValueChange={(val) => setEditPropertyForm({...editPropertyForm, furnishing: val})}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        <SelectItem value="unfurnished">Unfurnished</SelectItem>
                        <SelectItem value="part_furnished">Part Furnished</SelectItem>
                        <SelectItem value="furnished">Furnished</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="text-sm font-medium">Water System Type</label>
                  <Select value={editPropertyForm.water_system_type || 'none'} onValueChange={(val) => setEditPropertyForm({...editPropertyForm, water_system_type: val})}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Standard/No risk</SelectItem>
                      <SelectItem value="stored_water">Stored Water System</SelectItem>
                      <SelectItem value="complex_system">Complex Water System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-4">
                  <label className="text-sm font-medium">Council Licensing</label>
                  <p className="text-xs text-muted-foreground mb-1">Does this property require a council licence?</p>
                  <Select value={editPropertyForm.licensing_status || 'no'} onValueChange={(val) => setEditPropertyForm({...editPropertyForm, licensing_status: val})}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No licence required</SelectItem>
                      <SelectItem value="yes">Licence required</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Not sure? Check your local council's website or search on{' '}
                    <a href="https://www.gov.uk/find-local-council" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">GOV.UK</a>.
                  </p>
                </div>
              </div>

              {/* Admin-only fields */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold text-sm">Admin Controls</h4>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <Select value={editPropertyForm.status || ''} onValueChange={(val) => setEditPropertyForm({...editPropertyForm, status: val})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Assigned Landlord</label>
                  <Select value={editPropertyForm.landlord_id || ''} onValueChange={(val) => setEditPropertyForm({...editPropertyForm, landlord_id: val})}>
                    <SelectTrigger data-testid="edit-property-landlord"><SelectValue placeholder="Select landlord" /></SelectTrigger>
                    <SelectContent>
                      {landlords.map(l => <SelectItem key={l.id} value={l.id}>{l.name} ({l.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditProperty(null)}>Cancel</Button>
                <Button onClick={handleSaveProperty} disabled={saving} data-testid="save-property-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={reminderRulesOpen} onOpenChange={setReminderRulesOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Rent reminder rules</DialogTitle>
              <DialogDescription>
                Manual sends and the daily automatic reminder job use the same rent reminder rules.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 text-sm text-muted-foreground">
              <div>
                <p className="font-medium text-foreground">Upcoming</p>
                <p>Sent when a rent schedule is due in 3 days and the status is still due.</p>
              </div>
              <div>
                <p className="font-medium text-foreground">Due today</p>
                <p>Sent on the due date when the schedule remains marked as due.</p>
              </div>
              <div>
                <p className="font-medium text-foreground">Overdue</p>
                <p>Schedules past the due date move to overdue automatically. Overdue reminders can send at most once every 7 days for the same rent schedule.</p>
              </div>
              <div>
                <p className="font-medium text-foreground">Duplicate protection</p>
                <p>The same reminder type is not sent twice for the same rent schedule on the same day.</p>
              </div>
              <div>
                <p className="font-medium text-foreground">Historical schedules</p>
                <p>Paid schedules are treated as historical records and are not edited.</p>
              </div>
              <div className="flex justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setReminderRulesOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== EDIT RENT DIALOG ===== */}
        <Dialog open={!!editRent} onOpenChange={() => setEditRent(null)}>
          <DialogContent data-testid="edit-rent-dialog">
            <DialogHeader>
              <DialogTitle>Edit Rent Schedule</DialogTitle>
              <DialogDescription>{editRent?.property_title} - {editRent?.tenant_name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Amount (GBP)</label>
                <Input type="number" value={editRentForm.rent_amount || ''} onChange={(e) => setEditRentForm({...editRentForm, rent_amount: parseFloat(e.target.value) || 0})} data-testid="edit-rent-amount" />
              </div>
              <div>
                <label className="text-sm font-medium">Due Day of Month</label>
                <Input type="number" min="1" max="28" value={editRentForm.due_day_of_month || ''} onChange={(e) => setEditRentForm({...editRentForm, due_day_of_month: parseInt(e.target.value) || 1})} data-testid="edit-rent-due-day" />
              </div>
              <div>
                <label className="text-sm font-medium">Payment Method</label>
                <Select value={editRentForm.payment_method || 'bank_transfer'} onValueChange={(val) => setEditRentForm({...editRentForm, payment_method: val})}>
                  <SelectTrigger data-testid="edit-rent-payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="standing_order">Standing Order</SelectItem>
                    <SelectItem value="direct_debit">Direct Debit</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={editRentForm.status || ''} onValueChange={(val) => setEditRentForm({...editRentForm, status: val})}>
                  <SelectTrigger data-testid="edit-rent-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="due">Due</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea value={editRentForm.notes || ''} onChange={(e) => setEditRentForm({...editRentForm, notes: e.target.value})} rows={2} data-testid="edit-rent-notes" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditRent(null)}>Cancel</Button>
                <Button onClick={handleSaveRent} disabled={saving} data-testid="save-rent-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== EDIT ASSIGNMENT DIALOG ===== */}
        <Dialog open={!!editAssignment} onOpenChange={() => setEditAssignment(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-assignment-dialog">
            <DialogHeader>
              <DialogTitle>Edit Assignment</DialogTitle>
              <DialogDescription>Update tenancy dates and status without changing the linked portal tenant</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Assignment Account</label>
                <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                  {getAssignmentPortalTenantLabel(editAssignment)}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">To change tenancy participants, end this assignment and create a new email-based assignment or invite.</p>
              </div>
              <div>
                <label className="text-sm font-medium">Tenancy Participants</label>
                <div className="space-y-2 rounded-md border bg-muted/20 p-3 text-sm">
                  {getAssignmentTenants(editAssignment).map((tenant, index) => (
                    <div key={tenant.user_id || tenant.email || `edit-assignment-tenant-${index}`} className="rounded-md border border-border/60 bg-background px-3 py-2">
                      <div>{tenant.name || '-'}</div>
                      <div className="text-xs text-muted-foreground">{tenant.email || 'No email linked'}</div>
                    </div>
                  ))}
                  {getAssignmentTenants(editAssignment).length === 0 ? (
                    <div className="text-muted-foreground">No tenancy participants recorded.</div>
                  ) : null}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Property</label>
                <Select value={editAssignmentForm.property_id} onValueChange={(val) => setEditAssignmentForm({...editAssignmentForm, property_id: val})}>
                  <SelectTrigger data-testid="edit-assignment-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title} - {p.address}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Start Date</label>
                  <Input type="date" value={editAssignmentForm.start_date || ''} onChange={(e) => setEditAssignmentForm({...editAssignmentForm, start_date: e.target.value})} data-testid="edit-assignment-start" />
                </div>
                <div>
                  <label className="text-sm font-medium">End Date</label>
                  <Input type="date" value={editAssignmentForm.end_date || ''} onChange={(e) => setEditAssignmentForm({...editAssignmentForm, end_date: e.target.value})} data-testid="edit-assignment-end" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={editAssignmentForm.status} onValueChange={(val) => setEditAssignmentForm({...editAssignmentForm, status: val})}>
                  <SelectTrigger data-testid="edit-assignment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="ended">Ended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea value={editAssignmentForm.notes || ''} onChange={(e) => setEditAssignmentForm({...editAssignmentForm, notes: e.target.value})} rows={2} />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditAssignment(null)}>Cancel</Button>
                <Button onClick={handleSaveAssignment} disabled={saving} data-testid="save-assignment-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== CREATE USER DIALOG ===== */}
        <Dialog open={createUser} onOpenChange={setCreateUser}>
          <DialogContent data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle>Create User</DialogTitle>
              <DialogDescription>Add a new user to the platform</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Full Name</label>
                <Input value={createUserForm.name} onChange={(e) => setCreateUserForm({...createUserForm, name: e.target.value})} placeholder="Full Name (e.g. John Smith)" data-testid="create-user-name" />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input type="email" value={createUserForm.email} onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})} placeholder="Email" data-testid="create-user-email" />
              </div>
              <div>
                <label className="text-sm font-medium">Password</label>
                <Input type="password" value={createUserForm.password} onChange={(e) => setCreateUserForm({...createUserForm, password: e.target.value})} placeholder="Min 6 characters" data-testid="create-user-password" />
              </div>
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input value={createUserForm.phone} onChange={(e) => setCreateUserForm({...createUserForm, phone: e.target.value})} placeholder="Phone (optional)" data-testid="create-user-phone" />
              </div>
              <div>
                <label className="text-sm font-medium">Role</label>
                <Select value={createUserForm.role} onValueChange={(val) => setCreateUserForm({
                  ...createUserForm,
                  role: val,
                  ownership_type: val === 'landlord' ? (createUserForm.ownership_type || 'individual') : 'individual',
                  company_name: val === 'landlord' ? createUserForm.company_name : '',
                  company_registration: val === 'landlord' ? createUserForm.company_registration : '',
                })}>
                  <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="landlord">Landlord</SelectItem>
                    <SelectItem value="tenant">Tenant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {createUserForm.role === 'landlord' && (
                <>
                  <div>
                    <label className="text-sm font-medium">Ownership Type</label>
                    <Select
                      value={createUserForm.ownership_type || 'individual'}
                      onValueChange={(val) => {
                        setAdminUserErrors((prev) => ({ ...prev, create_company_name: '', create_company_registration: '' }));
                        setCreateUserForm({
                          ...createUserForm,
                          ownership_type: val,
                          company_name: val === 'company' ? createUserForm.company_name : '',
                          company_registration: val === 'company' ? createUserForm.company_registration : '',
                        });
                      }}
                    >
                      <SelectTrigger data-testid="create-user-ownership-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="company">Company</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {createUserForm.ownership_type === 'company' && (
                    <>
                      <div>
                        <label className="text-sm font-medium">Company Name</label>
                        <Input value={createUserForm.company_name || ''} onChange={(e) => {
                          setCreateUserForm({...createUserForm, company_name: e.target.value});
                          setAdminUserErrors((prev) => ({ ...prev, create_company_name: '' }));
                        }} placeholder="Company name" data-testid="create-user-company-name" />
                        {adminUserErrors.create_company_name ? <p className="mt-1 text-xs text-destructive">{adminUserErrors.create_company_name}</p> : null}
                      </div>
                      <div>
                        <label className="text-sm font-medium">Company Registration</label>
                        <Input value={createUserForm.company_registration || ''} onChange={(e) => {
                          setCreateUserForm({...createUserForm, company_registration: e.target.value});
                          setAdminUserErrors((prev) => ({ ...prev, create_company_registration: '' }));
                        }} placeholder="Company registration" data-testid="create-user-company-registration" />
                        {adminUserErrors.create_company_registration ? <p className="mt-1 text-xs text-destructive">{adminUserErrors.create_company_registration}</p> : null}
                      </div>
                    </>
                  )}
                </>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setCreateUser(false)}>Cancel</Button>
                <Button onClick={handleCreateUser} disabled={saving} data-testid="submit-create-user">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== CREATE PROPERTY DIALOG ===== */}
        <Dialog
          open={createProperty}
          onOpenChange={setCreateProperty}
        >
          <DialogContent data-testid="create-property-dialog">
            <DialogHeader>
              <DialogTitle>Add Property</DialogTitle>
              <DialogDescription>Register a new property on the platform</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Title</label>
                <Input value={createPropertyForm.title} onChange={(e) => setCreatePropertyForm({...createPropertyForm, title: e.target.value})} placeholder="Property name" data-testid="create-property-title" />
              </div>
              <div>
                <label className="text-sm font-medium">Address</label>
                <div className="space-y-2">
                  <AddressInput
                    defaultValue={createPropertyForm.address}
                    onAddressSelect={(addressObject) => {
                      setCreatePropertyForm((prev) => ({
                        ...prev,
                        address: Object.prototype.hasOwnProperty.call(addressObject || {}, 'formatted')
                          ? (addressObject?.formatted ?? '')
                          : prev.address,
                        postcode: addressObject?.postcode || prev.postcode,
                      }));
                    }}
                    data-testid="create-property-address"
                  />
                  {createPropertyForm.address?.trim() && (
                    <p className="text-xs text-muted-foreground">{createPropertyForm.address}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Postcode</label>
                  <Input value={createPropertyForm.postcode} onChange={(e) => setCreatePropertyForm({...createPropertyForm, postcode: e.target.value})} placeholder="Postcode" />
                </div>
                <div>
                  <label className="text-sm font-medium">Rent (GBP)</label>
                  <Input type="number" value={createPropertyForm.rent} onChange={(e) => setCreatePropertyForm({...createPropertyForm, rent: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Number of Floors</label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={createPropertyForm.floors}
                  onChange={(e) => setCreatePropertyForm({...createPropertyForm, floors: normalizeNonNegativeIntegerInput(e.target.value)})}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select value={createPropertyForm.property_type} onValueChange={(val) => setCreatePropertyForm({...createPropertyForm, property_type: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="create-property-is-occupied"
                    checked={Boolean(createPropertyForm.is_occupied)}
                    onChange={(e) => setCreatePropertyForm({...createPropertyForm, is_occupied: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="create-property-is-occupied" className="text-sm font-medium cursor-pointer">Property Occupied</label>
                </div>

              </div>
              <div>
                <label className="text-sm font-medium">Landlord</label>
                <Select value={createPropertyForm.landlord_id} onValueChange={(val) => setCreatePropertyForm({...createPropertyForm, landlord_id: val})}>
                  <SelectTrigger data-testid="create-property-landlord"><SelectValue placeholder="Select landlord" /></SelectTrigger>
                  <SelectContent>{landlords.map(l => <SelectItem key={l.id} value={l.id}>{l.name} ({l.email})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setCreateProperty(false)}>Cancel</Button>
                <Button onClick={handleCreateProperty} disabled={saving} data-testid="submit-create-property">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== CREATE ISSUE DIALOG ===== */}
        <Dialog open={createIssue} onOpenChange={setCreateIssue}>
          <DialogContent data-testid="create-issue-dialog">
            <DialogHeader>
              <DialogTitle>Create Issue</DialogTitle>
              <DialogDescription>Report a new maintenance issue</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Category</label>
                <Select value={createIssueForm.category} onValueChange={(val) => updateCreateIssueDetails({ category: val, sub_category: '', custom_title: '' })}>
                  <SelectTrigger data-testid="create-issue-category">
                    <SelectValue asChild>
                      <div className="flex items-center gap-2">
                        {SelectedAdminCategoryIcon ? <SelectedAdminCategoryIcon size={16} /> : null}
                        <span>{createIssueForm.category}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ISSUE_CATEGORY_OPTIONS.map((category) => {
                      const Icon = CATEGORY_ICONS[category];
                      return (
                        <SelectItem key={category} value={category}>
                          <div className="flex items-center gap-2">
                            {Icon ? <Icon size={16} /> : null}
                            <span>{category}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {createIssueSubCategoryOptions.length > 0 && (
                <div>
                  <label className="text-sm font-medium">Subcategory (Optional)</label>
                  <Select value={createIssueForm.sub_category || '__none__'} onValueChange={(val) => updateCreateIssueDetails({ sub_category: val === '__none__' ? '' : val })}>
                    <SelectTrigger data-testid="create-issue-subcategory"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {createIssueSubCategoryOptions.map((subCategory) => <SelectItem key={subCategory} value={subCategory}>{subCategory}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {createIssueForm.category === 'Other' && (
                <div>
                  <label className="text-sm font-medium">Custom Title</label>
                  <Input value={createIssueForm.custom_title} onChange={(e) => updateCreateIssueDetails({ custom_title: e.target.value })} placeholder="Enter a short custom issue title" data-testid="create-issue-custom-title" />
                </div>
              )}
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea value={createIssueForm.description} onChange={(e) => setCreateIssueForm({...createIssueForm, description: e.target.value})} rows={5} placeholder="Describe the issue" data-testid="create-issue-description" />
              </div>
              <div>
                <label className="text-sm font-medium">Property</label>
                <Select value={createIssueForm.property_id} onValueChange={(val) => setCreateIssueForm({...createIssueForm, property_id: val})}>
                  <SelectTrigger data-testid="create-issue-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title} - {p.address}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Priority</label>
                <Select value={createIssueForm.priority} onValueChange={(val) => setCreateIssueForm({...createIssueForm, priority: val})}>
                  <SelectTrigger data-testid="create-issue-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setCreateIssue(false)}>Cancel</Button>
                <Button onClick={handleCreateIssue} disabled={saving} data-testid="submit-create-issue">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== CREATE RENT DIALOG ===== */}
        <Dialog open={createRent} onOpenChange={setCreateRent}>
          <DialogContent data-testid="create-rent-dialog">
            <DialogHeader>
              <DialogTitle>Create Rent Schedule</DialogTitle>
              <DialogDescription>Set up a recurring rent schedule</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Property</label>
                <Select value={createRentForm.property_id} onValueChange={(val) => setCreateRentForm({...createRentForm, property_id: val, tenant_id: ''})}>
                  <SelectTrigger data-testid="create-rent-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title} - {p.address}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Active Tenancy Participant</label>
                <Select value={createRentForm.tenant_id} onValueChange={(val) => setCreateRentForm({...createRentForm, tenant_id: val})}>
                  <SelectTrigger data-testid="create-rent-tenant"><SelectValue placeholder="Select active tenant" /></SelectTrigger>
                  <SelectContent>
                    {getActiveAssignmentsForProperty(createRentForm.property_id).map((assignment) => (
                      <SelectItem key={assignment.id} value={assignment.tenant_id}>
                        {getAssignmentPortalTenantLabel(assignment)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">Only portal tenant accounts that belong to the active tenancy for this property can be selected here.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Amount (GBP)</label>
                  <Input type="number" value={createRentForm.rent_amount} onChange={(e) => setCreateRentForm({...createRentForm, rent_amount: parseFloat(e.target.value) || 0})} data-testid="create-rent-amount" />
                </div>
                <div>
                  <label className="text-sm font-medium">Due Day (1-28)</label>
                  <Input type="number" min="1" max="28" value={createRentForm.due_day_of_month} onChange={(e) => setCreateRentForm({...createRentForm, due_day_of_month: parseInt(e.target.value) || 1})} data-testid="create-rent-due-day" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Payment Method</label>
                <Select value={createRentForm.payment_method} onValueChange={(val) => setCreateRentForm({...createRentForm, payment_method: val})}>
                  <SelectTrigger data-testid="create-rent-payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="standing_order">Standing Order</SelectItem>
                    <SelectItem value="direct_debit">Direct Debit</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea value={createRentForm.notes} onChange={(e) => setCreateRentForm({...createRentForm, notes: e.target.value})} rows={2} placeholder="Optional notes" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setCreateRent(false)}>Cancel</Button>
                <Button onClick={handleCreateRent} disabled={saving} data-testid="submit-create-rent">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== CREATE ASSIGNMENT DIALOG ===== */}
        <Dialog open={createAssignment} onOpenChange={setCreateAssignment}>
          <DialogContent data-testid="create-assignment-dialog">
            <DialogHeader>
              <DialogTitle>Create Assignment</DialogTitle>
              <DialogDescription>Use the tenant email to link an existing tenant account or start the invite flow for a new email. Applications and assignments stay separate from tenancies until explicit finalization or invite acceptance.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Tenant Email</label>
                <Input
                  type="email"
                  value={createAssignmentForm.tenant_email}
                  onChange={(e) => setCreateAssignmentForm({...createAssignmentForm, tenant_email: e.target.value})}
                  placeholder="tenant@example.com"
                  data-testid="create-assignment-email"
                />
                <p className="mt-1 text-xs text-muted-foreground">Existing tenant account = instant assignment. New email = invite flow.</p>
              </div>
              <div>
                <label className="text-sm font-medium">Property</label>
                <Select value={createAssignmentForm.property_id} onValueChange={(val) => setCreateAssignmentForm({...createAssignmentForm, property_id: val})}>
                  <SelectTrigger data-testid="create-assignment-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title} - {p.address}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Start Date</label>
                <Input type="date" value={createAssignmentForm.start_date} onChange={(e) => setCreateAssignmentForm({...createAssignmentForm, start_date: e.target.value})} data-testid="create-assignment-start" />
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea value={createAssignmentForm.notes} onChange={(e) => setCreateAssignmentForm({...createAssignmentForm, notes: e.target.value})} rows={2} placeholder="Optional notes" />
              </div>
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <label className="text-sm font-medium">Send invite email automatically</label>
                  <p className="text-xs text-muted-foreground">Applies only when the email does not already belong to a tenant account.</p>
                </div>
                <Switch
                  checked={createAssignmentForm.send_invite_email !== false}
                  onCheckedChange={(checked) => setCreateAssignmentForm({...createAssignmentForm, send_invite_email: checked})}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setCreateAssignment(false)}>Cancel</Button>
                <Button onClick={handleCreateAssignment} disabled={saving} data-testid="submit-create-assignment">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== BACKUPS DIALOG ===== */}
        <Dialog open={viewingBackups} onOpenChange={setViewingBackups}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Database Backups</DialogTitle>
              <DialogDescription>All created backups listed most recent first.</DialogDescription>
            </DialogHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Backup ID</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backupsData.map((b, idx) => (
                  <TableRow key={b.id || idx}>
                    <TableCell className="text-xs font-mono">{b.id || '-'}</TableCell>
                    <TableCell className="text-sm">{b.created_at ? new Date(b.created_at).toLocaleString('en-GB') : '-'}</TableCell>
                    <TableCell><Badge className="bg-green-100 text-green-700">{b.status || 'created'}</Badge></TableCell>
                  </TableRow>
                ))}
                {backupsData.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">No backups found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>

        {/* ===== APPLICATION DETAIL DIALOG ===== */}
        <Dialog open={!!selectedApplication} onOpenChange={() => setSelectedApplication(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Application Detail</DialogTitle>
              <DialogDescription>{getPersonDisplayLabel(selectedApplication?.tenant_name, selectedApplication?.tenant_email)} → {selectedApplication?.property_title}</DialogDescription>
            </DialogHeader>
            {selectedApplication && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Status:</span> <span className="font-medium capitalize">{getApplicationDecisionStatus(selectedApplication)}</span></div>
                  <div><span className="text-muted-foreground">Date:</span> <span>{selectedApplication.created_at ? new Date(selectedApplication.created_at).toLocaleDateString('en-GB') : '-'}</span></div>
                  <div><span className="text-muted-foreground">Applicant:</span> <span className="font-medium">{getPersonDisplayLabel(selectedApplication.tenant_name, selectedApplication.tenant_email)}</span></div>
                  <div><span className="text-muted-foreground">Property:</span> <span>{selectedApplication.property_title || '-'}</span></div>
                  <div className="col-span-2"><span className="text-muted-foreground">Tenancy:</span> <span>No tenancy is created at the application stage.</span></div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm text-muted-foreground">
                    <span>Application conversation</span>
                    {selectedApplication.application_unread_count > 0 ? (
                      <Badge variant="outline">{selectedApplication.application_unread_count} unread</Badge>
                    ) : null}
                  </div>
                  <div className="space-y-3">
                    {getApplicationConversation(selectedApplication).map((message) => (
                      <div key={message.id || `${message.sender_role}-${message.created_at}`} className="rounded-lg border p-3 text-sm">
                        <div className="mb-1 flex items-center justify-between gap-3">
                          <span className="font-medium capitalize">{message.sender_role === 'tenant' ? 'Applicant' : message.sender_role || 'Message'}</span>
                          <span className="text-xs text-muted-foreground">
                            {message.created_at ? new Date(message.created_at).toLocaleString('en-GB') : 'Unknown time'}
                          </span>
                        </div>
                        <div className="whitespace-pre-wrap">{message.body || '-'}</div>
                      </div>
                    ))}
                    {getApplicationConversation(selectedApplication).length === 0 ? (
                      <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">No application messages recorded.</div>
                    ) : null}
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setSelectedApplication(null)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={deleteConfirm.open}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteConfirm((prev) => ({ ...prev, open: false, onConfirm: null }));
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{deleteConfirm.title}</AlertDialogTitle>
              <AlertDialogDescription>{deleteConfirm.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex justify-end gap-3">
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={deleteConfirm.isDestructive ? "bg-destructive hover:bg-destructive/90" : ""}
                onClick={async () => {
                  if (deleteConfirm.onConfirm) {
                    await deleteConfirm.onConfirm();
                  }
                  setDeleteConfirm((prev) => ({ ...prev, open: false, onConfirm: null }));
                }}
              >
                {deleteConfirm.actionLabel}
              </AlertDialogAction>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
